import React, { useState, useRef, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import html2canvas from "html2canvas";
import {
  FaCheckCircle,
  FaStar,
  FaBook,
  FaLeaf,
  FaAnchor,
  FaBell,
  FaHeart,
  FaBible,
  FaPrayingHands,
  FaChurch,
  FaCross,
  FaFish,
  FaDove,
  FaHandsHelping,
  FaGlobe,
  FaCrown,
  FaThLarge,
  FaGripHorizontal,
  FaPlay,
  FaPlayCircle,
  FaPause,
  FaChevronLeft,
  FaChevronRight,
  FaStepBackward,
  FaStepForward,
  FaTimes,
} from "react-icons/fa";
import * as FaIcons from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import * as GiIcons from "react-icons/gi";
import "./Infographics.css";
import useTouchNavigation from "../utils/useTouchNavigation";
import SiteWatermark from "./SiteWatermark";
import SEO from "./SEO";

// Canonical order for Bible books used to sort the Bible examples
const bibleBookOrder = [
  // Old Testament
  "Genesis",
  "Exodus",
  "Leviticus",
  "Numbers",
  "Deuteronomy",
  "Joshua",
  "Judges",
  "Ruth",
  "1_Samuel",
  "2_Samuel",
  "1_Kings",
  "2_Kings",
  "1_Chronicles",
  "2_Chronicles",
  "Ezra",
  "Nehemiah",
  "Esther",
  "Job",
  "Psalms",
  "Proverbs",
  "Ecclesiastes",
  "Song_of_Solomon",
  "Isaiah",
  "Jeremiah",
  "Lamentations",
  "Ezekiel",
  "Daniel",
  "Hosea",
  "Joel",
  "Amos",
  "Obadiah",
  "Jonah",
  "Micah",
  "Nahum",
  "Habakkuk",
  "Zephaniah",
  "Haggai",
  "Zechariah",
  "Malachi",
  // New Testament
  "Matthew",
  "Mark",
  "Luke",
  "John",
  "Acts",
  "Romans",
  "1_Corinthians",
  "2_Corinthians",
  "Galatians",
  "Ephesians",
  "Philippians",
  "Colossians",
  "1_Thessalonians",
  "2_Thessalonians",
  "1_Timothy",
  "2_Timothy",
  "Titus",
  "Philemon",
  "Hebrews",
  "James",
  "1_Peter",
  "2_Peter",
  "1_John",
  "2_John",
  "3_John",
  "Jude",
  "Revelation",
];
const SESSION_NAVIGATION_MESSAGE = "gcc-session-navigation";
const SESSION_NAVIGATION_RESPONSE = "gcc-session-navigation-response";
const SESSION_FOCUS_MESSAGE = "gcc-session-focus";

// Load available sample JSON files from the assets folder (including sub folders)
let sampleFiles = [];
try {
  const ctx = require.context("../assets/infographics", true, /\.json$/);
  sampleFiles = ctx.keys().map((key) => ({
    name: key.replace(/^\.\//, ""),
    data: ctx(key),
  }));
  // Exclude any files placed under the bible/old-testament folder so they are
  // not presented in the Infographics page UI
  sampleFiles = sampleFiles.filter(
    (f) => !f.name.startsWith("bible/old-testament/")
  );
  // Sort Bible examples in canonical order for easier selection
  sampleFiles.sort((a, b) => {
    const isBibleA = a.name.startsWith("bible/");
    const isBibleB = b.name.startsWith("bible/");
    if (isBibleA && isBibleB) {
      const nA = a.name.split("/").pop().replace(/\.json$/, "");
      const nB = b.name.split("/").pop().replace(/\.json$/, "");
      const iA = bibleBookOrder.indexOf(nA);
      const iB = bibleBookOrder.indexOf(nB);
      if (iA === -1 && iB === -1) return nA.localeCompare(nB);
      if (iA === -1) return 1;
      if (iB === -1) return -1;
      return iA - iB;
    }
    return a.name.localeCompare(b.name);
  });
} catch (err) {
  // Folder may not exist when building
  sampleFiles = [];
}

let bibleFiles = [];
try {
  const bctx = require.context(
    "../assets/infographics/bible",
    true,
    /\.json$/
  );
  bibleFiles = bctx.keys()
    .map((key) => ({
      name: key.replace(/^\.\//, ""),
      data: bctx(key),
    }))
    .sort((a, b) => {
      const nA = a.name.split("/").pop().replace(/\.json$/, "");
      const nB = b.name.split("/").pop().replace(/\.json$/, "");
      const iA = bibleBookOrder.indexOf(nA);
      const iB = bibleBookOrder.indexOf(nB);
      if (iA === -1 && iB === -1) return nA.localeCompare(nB);
      if (iA === -1) return 1;
      if (iB === -1) return -1;
      return iA - iB;
    });
} catch (err) {
  bibleFiles = [];
}

const colorSchemes = [
  {
    name: "sunset",
    gradStart: "#ff7e5f",
    gradEnd: "#feb47b",
    topicColor: "#2c3e50",
    subtopicColor: "#34495e",
    verseColor: "#34495e",
    iconColor: "#2c3e50",
    tileColor: "#ffe8d6",
  },
  {
    name: "ocean",
    gradStart: "#00c6ff",
    gradEnd: "#0072ff",
    topicColor: "#f0f7ff",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#e0f7ff",
  },
  {
    name: "lemon",
    gradStart: "#deff9c",
    gradEnd: "#54e346",
    topicColor: "#2d3436",
    subtopicColor: "#636e72",
    verseColor: "#636e72",
    iconColor: "#2d3436",
    tileColor: "#f2ffd9",
  },
  {
    name: "peach",
    gradStart: "#ff9966",
    gradEnd: "#ff5e62",
    topicColor: "#fff9f5",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#ffe0d2",
  },
  {
    name: "purpleDream",
    gradStart: "#9d50bb",
    gradEnd: "#6e48aa",
    topicColor: "#f6efff",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#ebd6f3",
  },
  {
    name: "forest",
    gradStart: "#5a8f38",
    gradEnd: "#2c5f2d",
    topicColor: "#edf7ed",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#d4e7c8",
  },
  {
    name: "sunrise",
    gradStart: "#ff512f",
    gradEnd: "#dd2476",
    topicColor: "#ffeef2",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#ffd9d2",
  },
  {
    name: "aqua",
    gradStart: "#13547a",
    gradEnd: "#80d0c7",
    topicColor: "#e7f6ff",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#cdeaf6",
  },
  {
    name: "steel",
    gradStart: "#485563",
    gradEnd: "#29323c",
    topicColor: "#f1f4f6",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#d0d6dc",
  },
  {
    name: "pastel",
    gradStart: "#a1c4fd",
    gradEnd: "#c2e9fb",
    topicColor: "#34495e",
    subtopicColor: "#5d6d7e",
    verseColor: "#5d6d7e",
    iconColor: "#34495e",
    tileColor: "#eef5ff",
  },
  {
    name: "midnight",
    gradStart: "#232526",
    gradEnd: "#414345",
    topicColor: "#eef2f5",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#616a70",
  },
  {
    name: "sunflower",
    gradStart: "#ffe259",
    gradEnd: "#ffa751",
    topicColor: "#2d3436",
    subtopicColor: "#555555",
    verseColor: "#555555",
    iconColor: "#2d3436",
    tileColor: "#fff8d7",
  },
  {
    name: "blush",
    gradStart: "#ff9a9e",
    gradEnd: "#fecfef",
    topicColor: "#2d3436",
    subtopicColor: "#5d5d5d",
    verseColor: "#5d5d5d",
    iconColor: "#2d3436",
    tileColor: "#ffe5e7",
  },
  {
    name: "cosmic",
    gradStart: "#0f2027",
    gradEnd: "#2c5364",
    topicColor: "#e5f1f7",
    subtopicColor: "#ffffff",
    verseColor: "#ffffff",
    iconColor: "#ffffff",
    tileColor: "#39444a",
  },
];

const subViewTypes = [
  "round",
  "list",
  "grid",
  "tile",
  "bubble",
  "mindmap",
  "tile-rel",
  "shadow",
  "glass",
  "outline",
  "card",
  "neumorphic",
  "gradient-border",

  "taglist",
  "circle",
  "hex",
  "timeline",
  "stack",
  "circle-card",
  "pill",
  "sketch",
  "frosted",
  "paper",
  "ribbon",
  "cloud",
  "note",
];

const lightBgViews = new Set([
  "tile",
  "tile-rel",
  "bubble",
  "shadow",
  "glass",
  "outline",
  "card",
  "neumorphic",
  "gradient-border",
  "round",
  "taglist",
  "circle",
  "hex",
  "pill",
  "sketch",
  "frosted",
  "circle-card",
  "paper",
  "ribbon",
  "cloud",
  "note",
]);

const iconMap = {
  checkcircle: FaCheckCircle,
  star: FaStar,
  book: FaBook,
  leaf: FaLeaf,
  anchor: FaAnchor,
  bell: FaBell,
  heart: FaHeart,
  bible: FaBible,
  prayinghands: FaPrayingHands,
  church: FaChurch,
  cross: FaCross,
  fish: FaFish,
  dove: FaDove,
  handshelping: FaHandsHelping,
  globe: FaGlobe,
  crown: FaCrown,
};

const PRESENTATION_DEFAULTS = {
  gradStart: "#050506",
  gradEnd: "#0a0a0c",
  topicColor: "#ffffff",
  subColor: "#f2f2f2",
  verseColor: "#e6e6e6",
  iconColor: "#ffffff",
  subTileColor: "#0b0b0e",
  tileBorderColor: "#141418",
};

function getIconByName(name) {
  if (!name) return null;
  if (FaIcons[name]) return FaIcons[name];
  if (GiIcons[name]) return GiIcons[name];
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return iconMap[key] || null;
}

function getIconForText(text, explicit) {
  const named = getIconByName(explicit);
  if (named) return named;
  if (!text) return FaStar;
  const t = text.toLowerCase();
  if (t.includes("bible") || t.includes("book")) return FaBook;
  if (t.includes("prayer") || t.includes("pray")) return FaPrayingHands;
  if (t.includes("church")) return FaChurch;
  if (t.includes("cross")) return FaCross;
  if (t.includes("fish")) return FaFish;
  if (t.includes("dove")) return FaDove;
  if (t.includes("help") || t.includes("service")) return FaHandsHelping;
  if (t.includes("world") || t.includes("mission")) return FaGlobe;
  if (t.includes("king") || t.includes("lord")) return FaCrown;
  if (t.includes("heart") || t.includes("love")) return FaHeart;
  if (t.includes("leaf") || t.includes("nature") || t.includes("tree"))
    return FaLeaf;
  if (t.includes("anchor") || t.includes("hope")) return FaAnchor;
  if (t.includes("music") || t.includes("worship") || t.includes("bell"))
    return FaBell;
  if (t.includes("check") || t.includes("done") || t.includes("complete"))
    return FaCheckCircle;
  if (t.includes("star")) return FaStar;
  return FaStar;
}

function toTitleCase(str) {
  if (!str) return "";
  return str
    .replace(/[’‘]/g, "'")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function normalizeItem(item) {
  if (typeof item === "string") {
    return { topic: toTitleCase(item), subtopics: [] };
  }
  if (Array.isArray(item)) {
    return item.map(normalizeItem);
  }
  if (typeof item === "object" && item !== null) {
    const { topic, title, text, subtopics, children, subs, ...rest } = item;
    if (topic || title || text) {
      return {
        topic: toTitleCase(topic || title || text || ""),
        subtopics: Array.isArray(subtopics || children || subs)
          ? (subtopics || children || subs).map(normalizeItem)
          : [],
        ...rest,
      };
    }
    const [t, s] = Object.entries(item)[0];
    return {
      topic: toTitleCase(t),
      subtopics: Array.isArray(s) ? s.map(normalizeItem) : [normalizeItem(s)],
    };
  }
  return { topic: "", subtopics: [] };
}

function parseInput(input) {
  if (!input) return { title: "", pages: [] };
  // Try JSON parsing first
  try {
    const data = JSON.parse(input);
    const title = data.title || "";
    if (Array.isArray(data.pages)) {
      const pages = data.pages.map((p) => ({
        title: p.pageTitle || p.title || "",
        tag: p.pageTag || p.tag || "",
        topics: (p.chapters || p.topics || []).map(normalizeItem),
      }));
      return { title, pages };
    } else if (Array.isArray(data)) {
      return {
        title: "",
        pages: [{ title: "", topics: data.map(normalizeItem) }],
      };
    } else if (!data.chapters && !data.topics) {
      return {
        title: "",
        pages: [{ title: "", topics: data.map(normalizeItem) }],
      };
    } else if (data.chapters && Array.isArray(data.chapters)) {
      return {
        title,
        pages: [{ title, topics: data.chapters.map(normalizeItem) }],
      };
    } else if (data.topics && Array.isArray(data.topics)) {
      return {
        title,
        pages: [{ title, topics: data.topics.map(normalizeItem) }],
      };
    } else if (typeof data === "object") {
      const topics = Object.entries(data)
        .filter(([k]) => k !== "title")
        .map(([topic, subs]) => ({
          topic: toTitleCase(topic),
          subtopics: Array.isArray(subs)
            ? subs.map(normalizeItem)
            : [normalizeItem(subs)],
        }));
      return { title, pages: [{ title, topics }] };
    }
  } catch (e) {
    // Not JSON - parse bullet list
  }

  const lines = input.split(/\r?\n/);
  const root = [];
  const stack = [{ subtopics: root }];
  lines.forEach((line) => {
    if (!line.trim()) return;
    const bulletReplaced = line.replace(/[\u2022\u25E6\u2023\u25AA]/g, "-");
    const match = bulletReplaced.match(/^(\s*)([-*]+)?\s*(.+)$/);
    if (!match) return;
    const indent = match[1].length;
    const bullets = match[2] || "";
    const text = match[3].trim();
    if (!text) return;
    let level = bullets ? bullets.length + Math.floor(indent / 2) : 0;
    if (level >= stack.length) {
      level = stack.length - 1;
    }
    while (stack.length - 1 > level) {
      stack.pop();
    }
    const node = { topic: toTitleCase(text), subtopics: [] };
    stack[level].subtopics.push(node);
    stack.push(node);
  });
  return { title: "", pages: [{ title: "", topics: root }] };
}

function adjustColor(col, amt) {
  if (!col) return col;
  let usePound = false;
  let color = col;
  if (color.startsWith("#")) {
    color = color.slice(1);
    usePound = true;
  }
  let num = parseInt(color, 16);
  let r = (num >> 16) + amt;
  r = Math.max(0, Math.min(255, r));
  let g = ((num >> 8) & 0x00ff) + amt;
  g = Math.max(0, Math.min(255, g));
  let b = (num & 0x0000ff) + amt;
  b = Math.max(0, Math.min(255, b));
  return (
    (usePound ? "#" : "") +
    ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
  );
}

function getContrastColor(col) {
  if (!col) return "#000000";
  let color = col.startsWith("#") ? col.slice(1) : col;
  if (color.length === 3) {
    color = color
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? "#000000" : "#ffffff";
}

function slugify(str) {
  return (str || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function Infographics() {
  const [input, setInput] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [topics, setTopics] = useState([]);
  const [pages, setPages] = useState([]);
  const [gradStart, setGradStart] = useState(PRESENTATION_DEFAULTS.gradStart);
  const [gradEnd, setGradEnd] = useState(PRESENTATION_DEFAULTS.gradEnd);
  const [topicColor, setTopicColor] = useState(
    PRESENTATION_DEFAULTS.topicColor,
  );
  const [subColor, setSubColor] = useState(PRESENTATION_DEFAULTS.subColor);
  const defaultVerseColor = PRESENTATION_DEFAULTS.verseColor;
  const [verseColor, setVerseColor] = useState(defaultVerseColor);
  const [subTileColor, setSubTileColor] = useState(
    PRESENTATION_DEFAULTS.subTileColor,
  );
  const defaultTileBorderColor = PRESENTATION_DEFAULTS.tileBorderColor;
  const [tileBorderColor, setTileBorderColor] = useState(
    defaultTileBorderColor,
  );
  const [iconColor, setIconColor] = useState(PRESENTATION_DEFAULTS.iconColor);
  const [masterColor, setMasterColor] = useState("#ff0000");
  const [iconSize, setIconSize] = useState(16);


  // base icon size for tiles
const tileIconSize  = iconSize;

// Oversized icons for slideshow play + play-by-topic
const slideIconSize = Math.round(iconSize * 10);
// Slightly larger subtopic icons for readability
const subIconSize = Math.round(iconSize * 2);


  const [textSize, setTextSize] = useState(14);
  // Default to 4 topics per page so pages are split by default
  const [topicsPerPage, setTopicsPerPage] = useState(6);
  const [groupBy, setGroupBy] = useState("page-topic");
  const [selectedScheme, setSelectedScheme] = useState("custom");
  const [subViewType, setSubViewType] = useState("round");
  const [cardWidth, setCardWidth] = useState(400);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [showVerses, setShowVerses] = useState(true);
  const [subsPerBox, setSubsPerBox] = useState(20);
  const [useSubGradient, setUseSubGradient] = useState(false);
  const [useSlideGradient, setUseSlideGradient] = useState(true);
  const [centerAlign, setCenterAlign] = useState(false);
  const [transparentBg, setTransparentBg] = useState(false);
  const [gifDelay, setGifDelay] = useState(2000);
  const [videoMode, setVideoMode] = useState("page");
  const [transitionEffect, setTransitionEffect] = useState("fade");
  const [slideData, setSlideData] = useState([]);
  const slideSelectionRef = useRef(null);
  const pageSelectionRef = useRef(null);
  const [slideSelectionParam, setSlideSelectionParam] = useState("");
  const [pageSelectionParam, setPageSelectionParam] = useState("");
  const gridRef = useRef(null);
  const pagesRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const skipQuerySyncRef = useRef(false);
  const queryInitialisedRef = useRef(false);
  const pendingModeRef = useRef(null);
  const lastModeParamRef = useRef(null);
  const lastSearchRef = useRef(location.search);
  const lastSyncedSearchRef = useRef(location.search.replace(/^\?/, ''));
  const [controlsOpen, setControlsOpen] = useState(false);
  const [jsonQuery, setJsonQuery] = useState("");
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [overlayControlsVisible, setOverlayControlsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [defaultSlideLoopDisabled, setDefaultSlideLoopDisabled] = useState(true);
  const [slideLoopDisabled, setSlideLoopDisabled] = useState(true);
  const [showTiles, setShowTiles] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedFile, setSelectedFile] = useState("");
  const [topicLevel1, setTopicLevel1] = useState("");
  const [topicLevel2, setTopicLevel2] = useState("");
  const [topicLevel3, setTopicLevel3] = useState("");
  const [topControlsVisible, setTopControlsVisible] = useState(true);
  const [presentationMode, setPresentationMode] = useState(null);
  const jsonPrevRef = useRef("");
  const jsonContentFromQueryRef = useRef("");
  const lastLoadedFileRef = useRef("");
  const autoLoadedFileRef = useRef("");

  const fileEntries = React.useMemo(
    () =>
      sampleFiles.map((file) => {
        const path = file.name.replace(/\.json$/, "");
        const parts = path.split("/").filter(Boolean);
        const level1 = parts[0] || "";
        let level2 = "";
        let remainder = "";

        if (parts.length === 2) {
          level2 = parts[1];
        } else if (parts.length > 2) {
          level2 = parts[1];
          remainder = parts.slice(2).join("/");
        }

        return {
          path,
          level1,
          level2,
          remainder,
        };
      }),
    [],
  );

  const getLevel2Options = React.useCallback(
    (level1) => {
      if (!level1) return [];
      const unique = new Set();
      fileEntries.forEach((entry) => {
        if (entry.level1 === level1 && entry.level2) {
          unique.add(entry.level2);
        }
      });
      return Array.from(unique).sort((a, b) => a.localeCompare(b));
    },
    [fileEntries],
  );

  const getLevel3Options = React.useCallback(
    (level1, level2) => {
      if (!level1 || !level2) return [];
      const unique = new Set();
      fileEntries.forEach((entry) => {
        if (
          entry.level1 === level1 &&
          entry.level2 === level2 &&
          entry.remainder
        ) {
          unique.add(entry.remainder);
        }
      });
      return Array.from(unique).sort((a, b) => a.localeCompare(b));
    },
    [fileEntries],
  );

  const level1Options = React.useMemo(() => {
    const unique = new Set();
    fileEntries.forEach((entry) => {
      if (entry.level1) unique.add(entry.level1);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [fileEntries]);

  const level2Options = React.useMemo(
    () => getLevel2Options(topicLevel1),
    [getLevel2Options, topicLevel1],
  );

  const level3Options = React.useMemo(
    () => getLevel3Options(topicLevel1, topicLevel2),
    [getLevel3Options, topicLevel1, topicLevel2],
  );

  const splitSelectedFile = React.useCallback((path) => {
    if (!path) return { level1: "", level2: "", level3: "" };
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 1) {
      return { level1: parts[0], level2: "", level3: "" };
    }
    if (parts.length === 2) {
      return { level1: parts[0], level2: parts[1], level3: "" };
    }
    return {
      level1: parts[0],
      level2: parts[1],
      level3: parts.slice(2).join("/"),
    };
  }, []);

  const resetPresentationState = React.useCallback(() => {
    setPresentationMode(null);
    setShowSlideshow(false);
    setIsPlaying(false);
    setCurrentSlide(0);
    setSlideLoopDisabled(defaultSlideLoopDisabled);
    pendingModeRef.current = null;
  }, [defaultSlideLoopDisabled]);

  const trimCollectionBySelection = React.useCallback((collection, selection) => {
    const list = Array.isArray(collection) ? collection : [];
    if (!list.length || !selection) {
      return { items: list, applied: false };
    }
    const maxIndex = list.length - 1;
    const start = Math.min(Math.max(selection.start, 0), maxIndex);
    const end = Math.min(Math.max(selection.end ?? selection.start, start), maxIndex);
    const sliced = list.slice(start, end + 1);
    if (!sliced.length) {
      return { items: list, applied: false };
    }
    return { items: sliced, applied: true };
  }, []);

  const applySlideSelection = React.useCallback(
    (allSlides) => {
      const { items, applied } = trimCollectionBySelection(allSlides, slideSelectionRef.current);
      return { slides: items, selectionApplied: applied };
    },
    [trimCollectionBySelection],
  );

  const findSampleBySlug = React.useCallback((nameOrSlug) => {
    if (!nameOrSlug) return null;
    const targetSlug = slugify(nameOrSlug);
    return (
      sampleFiles.find((file) => {
        const base = file.name.replace(/\.json$/, "");
        return slugify(base) === targetSlug;
      }) || null
    );
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--verse-color", verseColor);
  }, [verseColor]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--tile-border-color",
      tileBorderColor,
    );
  }, [tileBorderColor]);

  const applyMasterColor = (col) => {
    if (!col) return;
    setGradStart(adjustColor(col, -20));
    setGradEnd(adjustColor(col, 20));
    const contrast = getContrastColor(col);
    setTopicColor(contrast);
    setIconColor(adjustColor(col, -40));
    setSubColor(contrast === "#ffffff" ? "#eeeeee" : "#333333");
    setVerseColor(contrast === "#ffffff" ? "#dddddd" : "#555555");
    setSubTileColor(adjustColor(col, 80));
    setTileBorderColor(adjustColor(col, 40));
  };

  const handleGenerate = React.useCallback(
    (src) => {
      const dataStr = src !== undefined ? src : input;
      const parsed = parseInput(dataStr);
      setBookTitle(parsed.title || "");
      let newPages;
      if (parsed.pages && parsed.pages.length) {
        newPages = parsed.pages;
      } else {
        newPages = [{ title: parsed.title || "", topics: parsed.topics }];
      }

      if (topicsPerPage > 0) {
        newPages = newPages.flatMap((p) => {
          const chunks = [];
          for (let i = 0; i < p.topics.length; i += topicsPerPage) {
            chunks.push({
              title: p.title,
              topics: p.topics.slice(i, i + topicsPerPage),
            });
          }
          return chunks;
        });
      }

      setPages(newPages);
      setTopics(newPages[0]?.topics || []);
      setPageTitle(newPages[0]?.title || parsed.title || "");
    },
    [input, topicsPerPage],
  );

  const applySelectedFile = React.useCallback(
    (path) => {
      if (!path) return;
      setSelectedFile(path);
      const found = sampleFiles.find(
        (f) => f.name.replace(/\.json$/, "") === path,
      );
      if (found) {
        const jsonStr = JSON.stringify(found.data, null, 2);
        setInput(jsonStr);
        setJsonQuery(found.name.replace(/\.json$/, ""));
        handleGenerate(jsonStr);
        skipQuerySyncRef.current = true;
        setShowTiles(false);
      }
    },
    [handleGenerate],
  );

  const handleSchemeChange = (e) => {
    const value = e.target.value;
    setSelectedScheme(value);
    applyPresetScheme(value);
  };

  const handleViewChange = (e) => {
    setSubViewType(e.target.value);
  };

  const handleJsonInput = (e) => {
    const val = e.target.value;
    setJsonQuery(val);
    const found = sampleFiles.find(
      (f) => f.name.replace(/\.json$/, "") === val
    );
    if (found) {
      const jsonStr = JSON.stringify(found.data, null, 2);
      setInput(jsonStr);
      handleGenerate(jsonStr);
      setSelectedFile(found.name.replace(/\.json$/, ""));
    }
  };

  const focusJsonInput = (e) => {
    jsonPrevRef.current = jsonQuery;
    if (jsonQuery) setJsonQuery("");
    e.target.select();
    if (e.target.showPicker) {
      try {
        e.target.showPicker();
      } catch {
        /* ignored */
      }
    }
  };

  const restoreJsonInput = () => {
    if (!jsonQuery) setJsonQuery(jsonPrevRef.current);
  };

  const handleWidthChange = (e) => {
    setCardWidth(parseInt(e.target.value, 10));
  };

  const toggleDescriptions = () => {
    setShowDescriptions((prev) => !prev);
  };

  const toggleVerses = () => {
    setShowVerses((prev) => !prev);
  };

  const groupedFiles = React.useMemo(() => {
    const groups = {};
    sampleFiles.forEach((f) => {
      const parts = f.name.split('/');
      const folder = parts.length > 1 ? parts[0] : 'root';
      if (!groups[folder]) groups[folder] = [];
      groups[folder].push(f);
    });
    if (groups['bible']) {
      groups['bible'].sort((a, b) => {
        const nA = a.name.split('/').pop().replace(/\.json$/, '');
        const nB = b.name.split('/').pop().replace(/\.json$/, '');
        const iA = bibleBookOrder.indexOf(nA);
        const iB = bibleBookOrder.indexOf(nB);
        if (iA === -1 && iB === -1) return nA.localeCompare(nB);
        if (iA === -1) return 1;
        if (iB === -1) return -1;
        return iA - iB;
      });
    }
    return groups;
  }, []);

  const categories = React.useMemo(
    () => ["all", ...Object.keys(groupedFiles)],
    [groupedFiles],
  );

  const displayedGroups = React.useMemo(() => {
    const entries = Object.entries(groupedFiles);
    if (selectedCategory === "all") return entries;
    return entries.filter(([folder]) => folder === selectedCategory);
  }, [groupedFiles, selectedCategory]);

  const presentationBaseUrl = React.useMemo(() => {
    if (typeof window === "undefined") {
      return location.pathname;
    }
    return `${window.location.origin}${location.pathname}`;
  }, [location.pathname]);

  const playUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("json")) {
      params.delete("json");
    }
    params.set("mode", "play");
    const query = params.toString();
    return `${presentationBaseUrl}${query ? `?${query}` : ""}`;
  }, [location.search, presentationBaseUrl]);

  const playTopicUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("json")) {
      params.delete("json");
    }
    params.set("mode", "play-topic");
    const query = params.toString();
    return `${presentationBaseUrl}${query ? `?${query}` : ""}`;
  }, [location.search, presentationBaseUrl]);

  const parseSlideSelection = React.useCallback((value) => {
    if (!value) return null;
    const cleaned = String(value).replace(/\s+/g, "");
    if (/^\d+$/.test(cleaned)) {
      const idx = Number.parseInt(cleaned, 10);
      return Number.isFinite(idx) ? { start: idx - 1, end: idx - 1 } : null;
    }
    const rangeMatch = cleaned.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const start = Number.parseInt(rangeMatch[1], 10);
      const end = Number.parseInt(rangeMatch[2], 10);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        const normalizedStart = Math.max(1, start);
        const normalizedEnd = Math.max(normalizedStart, end);
        return { start: normalizedStart - 1, end: normalizedEnd - 1 };
      }
    }
    return null;
  }, []);

  const handleTileClick = (file) => {
    const jsonStr = JSON.stringify(file.data, null, 2);
    setInput(jsonStr);
    setJsonQuery(file.name.replace(/\.json$/, ""));
    handleGenerate(jsonStr);
    setShowTiles(false);
    setSelectedFile(file.name.replace(/\.json$/, ""));
  };

  const handleTopicLevel1Change = (event) => {
    const nextLevel1 = event.target.value;
    setTopicLevel1(nextLevel1);
    setTopicLevel2("");
    setTopicLevel3("");
    if (!nextLevel1) {
      setSelectedFile("");
      return;
    }
    const nextLevel2Options = getLevel2Options(nextLevel1);
    if (!nextLevel2Options.length) {
      applySelectedFile(nextLevel1);
      return;
    }
    const nextLevel2 = nextLevel2Options[0];
    setTopicLevel2(nextLevel2);
    const nextLevel3Options = getLevel3Options(nextLevel1, nextLevel2);
    if (nextLevel3Options.length) {
      const nextLevel3 = nextLevel3Options[0];
      setTopicLevel3(nextLevel3);
      applySelectedFile(`${nextLevel1}/${nextLevel2}/${nextLevel3}`);
      return;
    }
    setTopicLevel3("");
    applySelectedFile(`${nextLevel1}/${nextLevel2}`);
  };

  const handleTopicLevel2Change = (event) => {
    const nextLevel2 = event.target.value;
    setTopicLevel2(nextLevel2);
    setTopicLevel3("");
    if (!topicLevel1 || !nextLevel2) {
      return;
    }
    const nextLevel3Options = getLevel3Options(topicLevel1, nextLevel2);
    if (nextLevel3Options.length) {
      const nextLevel3 = nextLevel3Options[0];
      setTopicLevel3(nextLevel3);
      applySelectedFile(`${topicLevel1}/${nextLevel2}/${nextLevel3}`);
      return;
    }
    applySelectedFile(`${topicLevel1}/${nextLevel2}`);
  };

  const handleTopicLevel3Change = (event) => {
    const nextLevel3 = event.target.value;
    setTopicLevel3(nextLevel3);
    if (!topicLevel1 || !topicLevel2 || !nextLevel3) {
      return;
    }
    applySelectedFile(`${topicLevel1}/${topicLevel2}/${nextLevel3}`);
  };

  useEffect(() => {
    const { level1, level2, level3 } = splitSelectedFile(selectedFile);
    setTopicLevel1(level1);
    setTopicLevel2(level2);
    setTopicLevel3(level3);
  }, [selectedFile, splitSelectedFile]);

  useEffect(() => {
    if (selectedFile || topicLevel1 || level1Options.length === 0) return;
    setTopicLevel1(level1Options[0]);
  }, [level1Options, selectedFile, topicLevel1]);

  useEffect(() => {
    if (selectedFile || !topicLevel1 || topicLevel2 || level2Options.length === 0)
      return;
    setTopicLevel2(level2Options[0]);
  }, [level2Options, selectedFile, topicLevel1, topicLevel2]);

  useEffect(() => {
    if (
      selectedFile ||
      !topicLevel1 ||
      !topicLevel2 ||
      topicLevel3 ||
      level3Options.length === 0
    )
      return;
    setTopicLevel3(level3Options[0]);
  }, [level3Options, selectedFile, topicLevel1, topicLevel2, topicLevel3]);

  useEffect(() => {
    if (jsonContentFromQueryRef.current) return;
    if (selectedFile) {
      autoLoadedFileRef.current = selectedFile;
      return;
    }

    const params = new URLSearchParams(location.search);
    const titlesToTry = [];
    const paramBookTitle = params.get("bookTitle");
    const paramPageTitle = params.get("pageTitle");

    if (paramBookTitle) titlesToTry.push(paramBookTitle);
    if (paramPageTitle && paramPageTitle !== paramBookTitle)
      titlesToTry.push(paramPageTitle);

    let match = null;
    for (const title of titlesToTry) {
      const candidate = findSampleBySlug(title);
      if (candidate) {
        match = candidate;
        break;
      }
    }

    if (!match) {
      autoLoadedFileRef.current = "";
      return;
    }

    const baseName = match.name.replace(/\.json$/, "");
    if (autoLoadedFileRef.current === baseName) return;

    const jsonStr = JSON.stringify(match.data, null, 2);
    autoLoadedFileRef.current = baseName;
    lastLoadedFileRef.current = baseName;
    setInput(jsonStr);
    setJsonQuery(baseName);
    setSelectedFile(baseName);
    handleGenerate(jsonStr);

    if (!params.has("tiles")) {
      setShowTiles(false);
    }
  }, [
    findSampleBySlug,
    handleGenerate,
    location.search,
    selectedFile,
  ]);

  const toggleGroup = (folder) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [folder]: !prev[folder],
    }));
  };

  const parseBooleanParam = (value, fallback) => {
    if (value === null || value === undefined) return fallback;
    const normalized = String(value).trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    return fallback;
  };

  const parseNumberParam = (value, fallback) => {
    if (value === null || value === undefined || value === "") return fallback;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const normaliseModeParam = (value) => {
    if (!value) return null;
    const normalized = value.toLowerCase();
    if (["play", "slideshow", "slides"].includes(normalized)) return "play";
    if (["play-topic", "playtopic", "topic", "topics"].includes(normalized))
      return "play-topic";
    return null;
  };

  const applyPresetScheme = (value) => {
    if (!value) return;
    const preset = colorSchemes.find((g) => g.name === value);
    if (!preset) return;
    setGradStart(preset.gradStart);
    setGradEnd(preset.gradEnd);
    setTopicColor(preset.topicColor);
    setSubColor(preset.subtopicColor);
    setVerseColor(preset.verseColor || preset.subtopicColor);
    setIconColor(preset.iconColor || preset.topicColor);
    setSubTileColor(preset.tileColor || "");
    setTileBorderColor(adjustColor(preset.gradStart, 40));
    setMasterColor(preset.gradStart);
  };

const SlideshowOverlay = ({
  slides,
  onClose,
  controlsVisible,
  onToggleControls,
  disableLoop = false,
}) => {
  const intervalRef = useRef(null);
  const contentRef = useRef(null);
  const parentOriginRef = useRef("*");
  const isEmbeddedRef = useRef(false);
  const showControls = controlsVisible ?? false;
  const toggleControls = onToggleControls || (() => {});

  const currentSlideRef = useRef(0);
  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const embedded = window.parent && window.parent !== window;
    isEmbeddedRef.current = Boolean(embedded);
    if (!embedded) {
      return undefined;
    }
    if (typeof document !== "undefined" && document.referrer) {
      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (referrerOrigin) {
          parentOriginRef.current = referrerOrigin;
        }
      } catch (error) {
        parentOriginRef.current = "*";
      }
    }
    return undefined;
  }, []);
  const sendNavigationResponse = React.useCallback((requestId, action, handled) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_NAVIGATION_RESPONSE,
          payload: { requestId, action, handled },
        },
        targetOrigin,
      );
    } catch (error) {
      // ignore cross-origin failures
    }
  }, []);
  const sendFocusRequest = React.useCallback((target) => {
    if (!isEmbeddedRef.current || typeof window === "undefined") {
      return;
    }
    const targetOrigin = parentOriginRef.current || "*";
    try {
      window.parent.postMessage(
        {
          type: SESSION_FOCUS_MESSAGE,
          payload: { target },
        },
        targetOrigin,
      );
    } catch (error) {
      // ignore
    }
  }, []);

    const advanceSlide = React.useCallback(
      (direction = 1, options = {}) => {
        if (!slides.length) return;
        const lastIndex = slides.length - 1;
        if (disableLoop) {
          if (direction > 0) {
            if (currentSlideRef.current >= lastIndex) {
              if (options.fromAuto) {
                setIsPlaying(false);
              }
              return;
            }
            setCurrentSlide((prev) => Math.min(prev + 1, lastIndex));
          } else if (direction < 0) {
            if (currentSlideRef.current <= 0) {
              return;
            }
            setCurrentSlide((prev) => Math.max(prev - 1, 0));
          }
          return;
        }
        setCurrentSlide((prev) => {
          if (!slides.length) return prev;
          const nextIndex =
            (prev + direction + slides.length) % Math.max(slides.length, 1);
          return nextIndex;
        });
      },
      [disableLoop, slides.length, setIsPlaying],
    );

    const goToNextSlide = React.useCallback(() => {
      advanceSlide(1);
    }, [advanceSlide]);

    const goToPrevSlide = React.useCallback(() => {
      advanceSlide(-1);
    }, [advanceSlide]);

    const jumpToTopic = React.useCallback(
      (direction) => {
        if (!slides.length) return;
        if (disableLoop) {
          const lastIndex = slides.length - 1;
          for (let offset = 1; offset < slides.length; offset += 1) {
            const idx = currentSlide + direction * offset;
            if (idx < 0 || idx > lastIndex) break;
            const next = slides[idx];
            if (next && (next.type === "topic" || next.type === "topicCard")) {
              setCurrentSlide(idx);
              return;
            }
          }
          return;
        }
        setCurrentSlide((s) => {
          for (let offset = 1; offset < slides.length; offset += 1) {
            const idx =
              (s + direction * offset + slides.length) % slides.length;
            const next = slides[idx];
            if (next && (next.type === "topic" || next.type === "topicCard")) {
              return idx;
            }
          }
          return s;
        });
      },
      [slides, disableLoop, currentSlide],
    );

    useTouchNavigation(contentRef, {
      onLeft: goToPrevSlide,
      onRight: goToNextSlide,
    });
    useEffect(() => {
      const keyHandler = (e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowRight") goToNextSlide();
        if (e.key === "ArrowLeft") goToPrevSlide();
        if (e.key === " ") {
          e.preventDefault();
          setIsPlaying((p) => !p);
        }
        if (e.key === "Tab" && e.shiftKey) {
          e.preventDefault();
          sendFocusRequest("controls");
        }
        if (e.key === ">") setGifDelay((d) => Math.max(d - 500, 200));
        if (e.key === "<") setGifDelay((d) => Math.min(d + 500, 10000));
        if (e.key === "Control" && !e.repeat) {
          e.preventDefault();
          toggleControls();
        }
      };
      document.addEventListener("keydown", keyHandler);
      return () => {
        document.removeEventListener("keydown", keyHandler);
      };
    }, [goToNextSlide, goToPrevSlide, onClose, toggleControls]);

    useEffect(() => {
      if (!isEmbeddedRef.current || typeof window === "undefined") {
        return undefined;
      }
      const handleSessionNavigation = (event) => {
        if (!event || typeof event.data !== "object" || event.data.type !== SESSION_NAVIGATION_MESSAGE) {
          return;
        }
        const expectedOrigin = parentOriginRef.current;
        if (expectedOrigin !== "*" && event.origin && event.origin !== expectedOrigin) {
          return;
        }
        const { action, requestId } = event.data;
        if (!requestId) return;
        const isNext = action === "next";
        const isPrevious = action === "previous";
        if (!isNext && !isPrevious) return;
        const hasSlides = slides.length > 0;
        const canAdvance =
          hasSlides &&
          (disableLoop
            ? isNext
              ? currentSlideRef.current < slides.length - 1
              : currentSlideRef.current > 0
            : hasSlides);
        if (canAdvance) {
          if (isNext) {
            goToNextSlide();
          } else if (isPrevious) {
            goToPrevSlide();
          }
        }
        sendNavigationResponse(requestId, action, Boolean(canAdvance));
      };
      window.addEventListener("message", handleSessionNavigation);
      return () => window.removeEventListener("message", handleSessionNavigation);
    }, [disableLoop, goToNextSlide, goToPrevSlide, sendNavigationResponse, slides.length]);

    useEffect(() => {
      if (!isPlaying) return;
      intervalRef.current = setInterval(() => {
        advanceSlide(1, { fromAuto: true });
      }, gifDelay);
      return () => clearInterval(intervalRef.current);
    }, [advanceSlide, gifDelay, isPlaying]);

    const cur = slides[currentSlide] || { type: "title", text: "" };
    useEffect(() => {
      if (!slides.length) return;
      const payload = {
        type: "presentSlideUpdate",
        current: currentSlide + 1,
        total: slides.length,
      };
      if (typeof window !== "undefined") {
        try {
          window.postMessage(payload, "*");
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(payload, "*");
          }
        } catch (err) {
          // ignore cross-origin restrictions
        }
      }
    }, [currentSlide, slides.length]);
    const Icon = cur.icon;
    const heading =
      cur.type === "title" ? (
        <h1 style={{ color: cur.color || topicColor }}>{cur.text}</h1>
      ) : cur.type === "topic" ? (
        <h2 style={{ color: cur.color || topicColor }}>{cur.text}</h2>
      ) : (
        <h3 style={{ color: cur.color || subColor }}>{cur.text}</h3>
      );

    const scheme = colorSchemes.find((g) => g.name === selectedScheme) || {};
    let startColor = cur.gradStart || gradStart || scheme.gradStart;
    let endColor = cur.gradEnd || gradEnd || scheme.gradEnd;
    if (useSlideGradient) {
      if (!startColor && endColor) startColor = adjustColor(endColor, -20);
      if (!endColor && startColor) endColor = adjustColor(startColor, 20);
      if (startColor && endColor && startColor.toLowerCase() === endColor.toLowerCase()) {
        endColor = adjustColor(startColor, 20);
      }
    }

    const overlayStyle = useSlideGradient
      ? { background: `linear-gradient(135deg, ${startColor}, ${endColor})` }
      : { background: cur.tileColor || subTileColor || startColor || "#ffffff" };

    const sliderPercent = Math.max(
      0,
      Math.min(1, (gifDelay - 200) / (10000 - 200)),
    );

    return (
      <div className="slideshow-overlay" style={overlayStyle} onClick={onClose}>
        <div
          className={`slideshow-content ${cur.type}`}
          ref={contentRef}
          onClick={(e) => e.stopPropagation()}
        >
          {cur.type === "topicCard" ? (
            <>
              <div className="topic-card-header">
                {Icon && (
                  <Icon
                    className="slide-icon"
                    title={cur.text}
                    style={{
                      color: cur.iconColor || iconColor,
                      fontSize: slideIconSize,
                    }}
                  />
                )}
                {heading}
                {showVerses && cur.verse && (
                  <div className="slide-verse" style={{ color: cur.verseColor || verseColor }}>
                    {cur.verse}
                  </div>
                )}
              </div>
              {cur.subtopics && (
                <div className={`topic-card-subtopics subtopics ${subViewType}`}>
                  {cur.subtopics.map((s, idx) => {
                    const SIcon = s.icon;
                    const style = {};
                    if (useSubGradient && s.gradStart && s.gradEnd) {
                      style.background = `linear-gradient(135deg, ${s.gradStart}, ${s.gradEnd})`;
                    } else if (s.tileColor) {
                      style.background = s.tileColor;
                    } else if (subTileColor) {
                      style.background = subTileColor;
                    }
                    return (
                      <div key={idx} className="card-subtopic" style={style}>
                        {SIcon && (
                          <SIcon
                            className="subtopic-icon"
                            title={s.topic}
                            style={{
                              color: s.iconColor || iconColor,
                              fontSize: subIconSize,
                            }}
                          />
                        )}
                        <span style={{ color: s.color || subColor }}>{s.topic}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {Icon && (
                <Icon
                  className="slide-icon"
                  title={cur.text}
                  style={{
                    color: cur.iconColor || iconColor,
                    fontSize: slideIconSize,
                  }}
                />
              )}
              {heading}
              {showVerses && cur.verse && (
                <div className="slide-verse" style={{ color: cur.verseColor || verseColor }}>
                  {cur.verse}
                </div>
              )}
            </>
          )}
          <div className={`slideshow-bottom-nav ${showControls ? "visible" : ""}`}>
            <div className="nav-row">
              <button
                type="button"
                className="nav-pill"
                onClick={() => jumpToTopic(-1)}
                title="Previous topic"
                aria-label="Previous topic"
              >
                <FaStepBackward />
              </button>
              <button
                type="button"
                className="nav-pill"
                onClick={goToPrevSlide}
                title="Previous slide"
                aria-label="Previous slide"
              >
                <FaChevronLeft />
              </button>
              <div className="nav-play-wrapper">
                <span className="slideshow-counter">
                  {currentSlide + 1} / {slides.length}
                </span>
                <button
                  type="button"
                  className="play-toggle"
                  onClick={() => setIsPlaying((p) => !p)}
                  title={isPlaying ? "Pause autoplay" : "Play autoplay"}
                  aria-label={isPlaying ? "Pause autoplay" : "Play autoplay"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>
              </div>
              <button
                type="button"
                className="nav-pill"
                onClick={goToNextSlide}
                title="Next slide"
                aria-label="Next slide"
              >
                <FaChevronRight />
              </button>
              <button
                type="button"
                className="nav-pill"
                onClick={() => jumpToTopic(1)}
                title="Next topic"
                aria-label="Next topic"
              >
                <FaStepForward />
              </button>
              <div className="nav-slider">
                <input
                  type="range"
                  min="200"
                  max="10000"
                  step="200"
                  value={gifDelay}
                  onChange={(e) => setGifDelay(parseInt(e.target.value, 10))}
                  style={{
                    background: `linear-gradient(90deg, var(--present-panel-border) ${sliderPercent *
                      100}%, rgba(255,255,255,0.18) ${sliderPercent * 100}%)`,
                  }}
                />
                <span className="speed-display">{(gifDelay / 1000).toFixed(1)}s</span>
              </div>
            </div>
          </div>
          <div className="slide-counter-badge" aria-live="polite">
            {currentSlide + 1} / {slides.length}
          </div>
        </div>
      </div>
    );
  };

  const handleExport = () => {
    if (!gridRef.current) return;
    const cards = gridRef.current.querySelectorAll(".infographic-card");
    cards.forEach((card, index) => {
      let originalBg;
      if (transparentBg) {
        originalBg = card.style.background;
        card.style.background = "none";
      }
      html2canvas(card, {
        backgroundColor: transparentBg ? null : undefined,
      }).then((canvas) => {
        if (transparentBg) {
          card.style.background = originalBg;
        }
        const link = document.createElement("a");
        const topicEl = card.querySelector(".topic-header");
        const subEl = card.querySelector(".subtopic span");
        const topic = topicEl
          ? topicEl.textContent.trim()
          : `topic-${index + 1}`;
        const sub = subEl ? subEl.textContent.trim() : "";
        const fileName =
          [
            slugify(bookTitle),
            slugify(displayPages[0]?.title || "page-1"),
            slugify(topic),
            sub ? slugify(sub) : null,
            `page-1`,
            `card-${index + 1}`,
          ]
            .filter(Boolean)
            .join("_") + ".png";
        link.download = fileName;
        link.href = canvas.toDataURL("image/png");
        link.click();
      });
    });
  };

  const handleExportAll = () => {
    if (!pagesRef.current) return;
    const cards = pagesRef.current.querySelectorAll(".infographic-card");
    const originals = [];
    if (transparentBg) {
      cards.forEach((card) => {
        originals.push(card.style.background);
        card.style.background = "none";
      });
    }
    html2canvas(pagesRef.current, {
      backgroundColor: transparentBg ? null : undefined,
    }).then((canvas) => {
      if (transparentBg) {
        cards.forEach((card, idx) => {
          card.style.background = originals[idx];
        });
      }
      const link = document.createElement("a");
      const name =
        [slugify(bookTitle), "all-pages"].filter(Boolean).join("_") + ".png";
      link.download = name;
      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  const handleExportPages = () => {
    if (!pagesRef.current) return;
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    pageEls.forEach((page, index) => {
      const cards = page.querySelectorAll(".infographic-card");
      const originals = [];
      if (transparentBg) {
        cards.forEach((card) => {
          originals.push(card.style.background);
          card.style.background = "none";
        });
      }
      html2canvas(page, {
        backgroundColor: transparentBg ? null : undefined,
      }).then((canvas) => {
        if (transparentBg) {
          cards.forEach((card, idx) => {
            card.style.background = originals[idx];
          });
        }
        const link = document.createElement("a");
        const title = displayPages[index]?.title || `page-${index + 1}`;
        const name =
          [slugify(bookTitle), slugify(title), `page-${index + 1}`]
            .filter(Boolean)
            .join("_") + ".png";
        link.download = name;
        link.href = canvas.toDataURL("image/png");
        link.click();
      });
    });
  };
  const exportPagesData = async () => {
    if (!pagesRef.current) return [];
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    const result = [];
    for (let i = 0; i < pageEls.length; i++) {
      const page = pageEls[i];
      const cards = page.querySelectorAll(".infographic-card");
      const originals = [];
      if (transparentBg) {
        cards.forEach((card) => {
          originals.push(card.style.background);
          card.style.background = "none";
        });
      }
      const canvas = await html2canvas(page, {
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: true,
        backgroundColor: transparentBg ? null : undefined,
      });
      if (transparentBg) {
        cards.forEach((card, idx) => {
          card.style.background = originals[idx];
        });
      }
      result.push(canvas.toDataURL("image/png"));
    }
    return result;
  };
  const loadJsPdf = () => {
    if (window.jspdf) return Promise.resolve(window.jspdf);
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      script.onload = () => resolve(window.jspdf);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const handleExportPDF = async () => {
    if (!pagesRef.current) return;
    const { jsPDF } = await loadJsPdf();
    const pdf = new jsPDF();
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    for (let i = 0; i < pageEls.length; i++) {
      const pageEl = pageEls[i];
      const cards = pageEl.querySelectorAll(".infographic-card");
      const originals = [];
      if (transparentBg) {
        cards.forEach((card) => {
          originals.push(card.style.background);
          card.style.background = "none";
        });
      }
      const canvas = await html2canvas(pageEl, {
        backgroundColor: transparentBg ? null : undefined,
      });
      if (transparentBg) {
        cards.forEach((card, idx) => {
          card.style.background = originals[idx];
        });
      }
      const imgData = canvas.toDataURL("image/png");
      const imgProps = pdf.getImageProperties(imgData);
      const scale = Math.min(pageW / imgProps.width, pageH / imgProps.height);
      const w = imgProps.width * scale;
      const h = imgProps.height * scale;
      const x = (pageW - w) / 2;
      const y = 0;
      if (i > 0) pdf.addPage();
      pdf.setFillColor(gradStart || "#ffffff");
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.addImage(imgData, "PNG", x, y, w, h);
    }
    const name =
      [slugify(bookTitle) || "present"].filter(Boolean).join("_") + ".pdf";
    pdf.save(name);
  };

  const handleExportBible = async () => {
    if (bibleFiles.length === 0) return;
    // Ensure any running slideshow is stopped so pages render correctly
    resetPresentationState();
    const prevInput = input;
    const prevFile = selectedFile;
    const prevQuery = jsonQuery;
    try {
      const { jsPDF } = await loadJsPdf();
      const pdf = new jsPDF();
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      let pageIndex = 0;
      for (const file of bibleFiles) {
        const jsonStr = JSON.stringify(file.data, null, 2);
        setInput(jsonStr);
        handleGenerate(jsonStr);
        // Give React time to render the new pages before capturing
        await new Promise((r) => setTimeout(r, 200));
        const images = await exportPagesData();
        for (const imgData of images) {
          const imgProps = pdf.getImageProperties(imgData);
          const scale = Math.min(pageW / imgProps.width, pageH / imgProps.height);
          const w = imgProps.width * scale;
          const h = imgProps.height * scale;
          const x = (pageW - w) / 2;
          const y = 0;
          if (pageIndex > 0) pdf.addPage();
          pdf.setFillColor(gradStart || "#ffffff");
          pdf.rect(0, 0, pageW, pageH, "F");
          pdf.addImage(imgData, "PNG", x, y, w, h);
          pageIndex++;
        }
      }
      pdf.save("bible.pdf");
    } catch (err) {
      console.error("Export Bible failed", err);
    } finally {
      setInput(prevInput);
      setSelectedFile(prevFile);
      setJsonQuery(prevQuery);
      handleGenerate(prevInput);
    }
  };

  const loadGifshot = () => {
    if (window.gifshot) return Promise.resolve(window.gifshot);
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/gifshot/0.3.2/gifshot.min.js";
      script.onload = () => resolve(window.gifshot);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const loadPptxGen = async () => {
    if (window.PptxGenJS) return window.PptxGenJS;
    try {
      const mod = await import("pptxgenjs");
      return mod.default || mod;
    } catch (err) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/PptxGenJS/3.9.0/pptxgen.min.js";
        script.onload = () => resolve(window.PptxGenJS);
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const loadWhammy = async () => {
    if (window.Whammy) return window.Whammy;
    try {
      const mod = await import("whammy");
      return mod.default || mod;
    } catch (err) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/whammy/0.0.2/whammy.min.js";
        script.onload = () => resolve(window.Whammy);
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const waitForImages = async (el) => {
    const imgs = el.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => {
              const done = () => {
                img.removeEventListener("load", done);
                img.removeEventListener("error", done);
                res();
              };
              img.addEventListener("load", done);
              img.addEventListener("error", done);
            }),
      ),
    );
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  };

  const captureCanvas = async (el) => {
    const canvas = await html2canvas(el, {

      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: true,
      x: 0, // capture from left
      
      y: 0, // capture from top
      
      width: 960,
      
      height:540,
      backgroundColor: transparentBg ? null : undefined,
  
    });
    return canvas;
  };

  const handleExportGIF = async () => {
    if (!pagesRef.current) return;
    const gifshot = await loadGifshot();
    const cards = pagesRef.current.querySelectorAll(".infographic-card");
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const subs = Array.from(card.querySelectorAll(".subtopic"));
      const frames = [];
      subs.forEach((s) => (s.style.visibility = "hidden"));
      const first = await captureCanvas(card);
      frames.push(first.toDataURL("image/png"));
      for (let j = 0; j < subs.length; j++) {
        subs[j].style.visibility = "visible";
        const c = await captureCanvas(card);
        frames.push(c.toDataURL("image/png"));
      }
      subs.forEach((s) => (s.style.visibility = "visible"));
      await new Promise((res) => {
        gifshot.createGIF(
          {
            images: frames,
            gifWidth: first.width,
            gifHeight: first.height,
            interval: gifDelay / 1000,
          },
          (obj) => {
            if (!obj.error) {
              const link = document.createElement("a");
              link.href = obj.image;
              const pageEl = card.closest(".infographic-page");
              const pageIdx = Array.from(pageEls).indexOf(pageEl);
              const topicEl = card.querySelector(".topic-header");
              const subEl = card.querySelector(".subtopic span");
              const topic = topicEl
                ? topicEl.textContent.trim()
                : `topic-${i + 1}`;
              const sub = subEl ? subEl.textContent.trim() : "";
              const fileName =
                [
                  slugify(bookTitle),
                  slugify(
                    displayPages[pageIdx]?.title || `page-${pageIdx + 1}`,
                  ),
                  slugify(topic),
                  sub ? slugify(sub) : null,
                  `page-${pageIdx + 1}`,
                  `card-${i + 1}`,
                ]
                  .filter(Boolean)
                  .join("_") + ".gif";
              link.download = fileName;
              link.click();
            }
            res();
          },
        );
      });
    }
  };

  const handleExportPPTX = async () => {
    if (!pagesRef.current) return;
    const PptxGenJS = await loadPptxGen();
    const pptx = new PptxGenJS();
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    for (let i = 0; i < pageEls.length; i++) {
      const canvas = await captureCanvas(pageEls[i]);
      const slide = pptx.addSlide();
      const imgW = canvas.width;
      const imgH = canvas.height;
      const sW = pptx.width;
      const sH = pptx.height;
      const scale = Math.min(sW / imgW, sH / imgH);
      const w = imgW * scale;
      const h = imgH * scale;
      const x = (sW - w) / 2;
      const y = 0;
      slide.addImage({ data: canvas.toDataURL("image/png"), x, y, w, h });
      const cards = pageEls[i].querySelectorAll(".infographic-card");
      for (let j = 0; j < cards.length; j++) {
        const c = await captureCanvas(cards[j]);
        const img2W = c.width;
        const img2H = c.height;
        const scale2 = Math.min(sW / img2W, sH / img2H);
        const w2 = img2W * scale2;
        const h2 = img2H * scale2;
        const x2 = (sW - w2) / 2;
        pptx
          .addSlide()
          .addImage({
            data: c.toDataURL("image/png"),
            x: x2,
            y: 0,
            w: w2,
            h: h2,
          });
      }
    }
    const name =
      [slugify(bookTitle) || "present"].filter(Boolean).join("_") +
      ".pptx";
    await pptx.writeFile(name);
  };

  const handleExportDOC = async () => {
    if (!pagesRef.current) return;
    const pageEls = pagesRef.current.querySelectorAll(".infographic-page");
    let html = `<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'></head><body>`;
    for (let i = 0; i < pageEls.length; i++) {
      const canvas = await captureCanvas(pageEls[i]);
      html += `<img src="${canvas.toDataURL("image/png")}" style="width:100%;" /><br/>`;
    }
    html += "</body></html>";
    const blob = new Blob([html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      [slugify(bookTitle) || "present"].filter(Boolean).join("_") + ".doc";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportVideo = async () => {
    if (!pagesRef.current) return;
    const Whammy = await loadWhammy();
    const fps = 2;
    const video = new Whammy.Video(fps);
    const selector =
      videoMode === "page" ? ".infographic-page" : ".infographic-card";
    const els = pagesRef.current.querySelectorAll(selector);
    for (let i = 0; i < els.length; i++) {
      const canvas = await captureCanvas(els[i]);
      video.add(canvas);
    }
    const output = video.compile();
    const url = URL.createObjectURL(output);
    const link = document.createElement("a");
    link.href = url;
    const videoName =
      [slugify(bookTitle) || "present"].filter(Boolean).join("_") +
      ".webm";
    link.download = videoName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const slideMarkup = (slide, capture = false) => {
    const Icon = slide.icon;
    const heading =
      slide.type === "title" ? (
        <h1 style={{ color: slide.color || topicColor }}>{slide.text}</h1>
      ) : slide.type === "topic" ? (
        <h2 style={{ color: slide.color || topicColor }}>{slide.text}</h2>
      ) : slide.type === "topicCard" ? (
        <h2 style={{ color: slide.color || topicColor }}>{slide.text}</h2>
      ) : (
        <h3 style={{ color: slide.color || subColor }}>{slide.text}</h3>
      );

    const scheme = colorSchemes.find((g) => g.name === selectedScheme) || {};
    let startColor = slide.gradStart || gradStart || scheme.gradStart;
    let endColor = slide.gradEnd || gradEnd || scheme.gradEnd;
    if (useSlideGradient) {
      if (!startColor && endColor) startColor = adjustColor(endColor, -20);
      if (!endColor && startColor) endColor = adjustColor(startColor, 20);
      if (
        startColor &&
        endColor &&
        startColor.toLowerCase() === endColor.toLowerCase()
      ) {
        endColor = adjustColor(startColor, 20);
      }
    }
    const overlayStyle = useSlideGradient
      ? { background: `linear-gradient(135deg, ${startColor}, ${endColor})` }
      : { background: slide.tileColor || subTileColor || startColor || "#ffffff" };

    const captureStyle = capture
      ? { position: "relative", width: "960px", height: "540px" }
      : {};

    return (
      <div className="slideshow-overlay" style={{ ...overlayStyle, ...captureStyle }}>
        <div className={`slideshow-content ${slide.type}`}>
          <img
            src={`${process.env.PUBLIC_URL}/favicon.ico`}
            alt="logo"
            className="slideshow-logo"
          />
          {slide.type === "topicCard" ? (
            <>
              <div className="topic-card-header">
                {Icon && (
                  <Icon
                    className="slide-icon"
                    title={slide.text}
                    style={{
                      color: slide.iconColor || iconColor,
                      fontSize: slideIconSize,
                    }}
                  />
                )}
                {heading}
                {showVerses && slide.verse && (
                  <div
                    className="slide-verse"
                    style={{ color: slide.verseColor || verseColor }}
                  >
                    {slide.verse}
                  </div>
                )}
              </div>
              {slide.subtopics && (
                <div className={`topic-card-subtopics subtopics ${subViewType}`}>
                  {slide.subtopics.map((s, idx) => {
                    const SIcon = s.icon;
                    const style = {};
                    if (useSubGradient && s.gradStart && s.gradEnd) {
                      style.background = `linear-gradient(135deg, ${s.gradStart}, ${s.gradEnd})`;
                    } else if (s.tileColor) {
                      style.background = s.tileColor;
                    } else if (subTileColor) {
                      style.background = subTileColor;
                    }
                    return (
                      <div key={idx} className="card-subtopic" style={style}>
                        {SIcon && (
                          <SIcon
                            className="subtopic-icon"
                            title={s.topic}
                            style={{
                              color: s.iconColor || iconColor,
                              fontSize: subIconSize,
                            }}
                          />
                        )}
                        <span style={{ color: s.color || subColor }}>{s.topic}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {Icon && (
                <Icon
                  className="slide-icon"
                  title={slide.text}
                  style={{
                    color: slide.iconColor || iconColor,
                    fontSize: slideIconSize,
                  }}
                />
              )}
              {heading}
              {showVerses && slide.verse && (
                <div
                  className="slide-verse"
                  style={{ color: slide.verseColor || verseColor }}
                >
                  {slide.verse}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const exportSlidesPPTX = async (arr, suffix) => {
    // 1) Load PptxGenJS
    const PptxGenJS = await loadPptxGen();
    const pptx = new PptxGenJS();
  
    // 2) Create an off-screen container that's layout-visible but never flashes
    const container = document.createElement("div");
    container.style.position      = "fixed";
    container.style.top           = "0";
    container.style.left          = "-10000px";  // off the left edge
    container.style.width         = "960px";
    container.style.height        = "540px";
    container.style.opacity       = "0.02";      // nearly invisible
    container.style.pointerEvents = "none";
    // DO NOT set visibility:hidden here, html2canvas must see layout
    document.body.appendChild(container);
  
    for (let i = 0; i < arr.length; i++) {
      // 3) Render the slide markup into the container
      container.innerHTML = ReactDOMServer.renderToStaticMarkup(
        slideMarkup(arr[i], true)
      );
      await waitForImages(container);
  
      // 4) SHOW it for one frame so html2canvas can measure & paint
      container.style.visibility = "visible";
      await new Promise(r => requestAnimationFrame(r));
  
      // 5) CAPTURE with html2canvas
      const canvas = await html2canvas(container.firstChild, {
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: true,
        backgroundColor: null,
      });
  
      // and immediately hide it again
      container.style.visibility = "hidden";
  
      // 6) Embed into PPTX
      const imgData = canvas.toDataURL("image/png");
      console.log("Captured image data:", imgData.slice(0,100)); // sanity check
  
      const imgW = canvas.width;
      const imgH = canvas.height;
      const scale = Math.min(pptx.width / imgW, pptx.height / imgH);
      const w = imgW * scale;
      const h = imgH * scale;
      const x = (pptx.width - w) / 2;
  
      const slide = pptx.addSlide();
      slide.addImage({ data: imgData, x, y: 0, w, h });
    }
  
    // 7) Clean up
    document.body.removeChild(container);
  
    // 8) Write file using the new API
    await pptx.writeFile({
      fileName: `${slugify(bookTitle) || "present"}_${suffix}.pptx`
    });
  };
  

  const handlePlayPPTX = async () => {
    await exportSlidesPPTX(slides, "play");
  };

  const handlePlayTopicPPTX = async () => {
    await exportSlidesPPTX(topicSlides, "topic");
  };

  const renderSubtopics = (
    subs = [],
    depth = 0,
    color = subColor,
    gStart,
    gEnd,
    iconClr = iconColor,
  ) => {
    if (!subs.length) return null;
    return (
      <div
        className={`subtopics ${subViewType}`}
        style={{ marginLeft: depth * 20 }}
      >
        {subs.map((sub, i) => {
          const data =
            typeof sub === "string" ? { topic: sub, subtopics: [] } : sub;
          const Icon = getIconForText(data.topic, data.icon);
          const itemColor = data.color || color;
          const itemIconColor = data.iconColor || iconClr || itemColor;
          const style = {};
          let start = data.gradStart;
          let end = data.gradEnd;
          if (!start && useSubGradient && gStart && gEnd) {
            start = adjustColor(gStart, 30);
            end = adjustColor(gEnd, 30);
          }
          if (start && end) {
            style.background = `linear-gradient(135deg, ${start}, ${end})`;
          } else if (subTileColor) {
            style.background = subTileColor;
          }
          return (
            <div
              className="subtopic"
              key={i}
              style={{
                ...style,
                ...(centerAlign
                  ? {
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }
                  : {}),
              }}
            >
              <Icon
                className="subtopic-icon"
                title={data.topic}
                style={{ color: itemIconColor, fontSize: subIconSize }}
              />
              <span style={{ color: itemColor, fontSize: textSize }}>
                {data.topic}
              </span>
              {showVerses && data.verse && (
                <div
                  className="subtopic-verse"
                  style={{
                    fontSize: textSize - 4,
                    color: verseColor,
                    ...(centerAlign
                      ? { marginLeft: 0, textAlign: "center" }
                      : {}),
                  }}
                >
                  {data.verse}
                </div>
              )}
              {showDescriptions && data.description && (
                <div
                  className="subtopic-desc"
                  style={{ fontSize: textSize - 2 }}
                >
                  {data.description}
                </div>
              )}
              {data.relations && subViewType.includes("rel") && (
                <div className="relations">
                  {data.relations.map((r, idx) => (
                    <span className="relation" key={idx}>
                      {r}
                    </span>
                  ))}
                </div>
              )}
              {renderSubtopics(
                data.subtopics || [],
                depth + 1,
                itemColor,
                gStart,
                gEnd,
                itemIconColor,
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const displayPages = React.useMemo(() => {
    let basePages;
    if (groupBy === "none") {
      const subs = pages.flatMap((p) =>
        (p.topics || []).flatMap((t) => t.subtopics || []),
      );
      basePages = [
        {
          title: pageTitle || bookTitle || pages[0]?.title || "",
          topics: [
            {
              topic: "",
              subtopics: subs.map((s) =>
                typeof s === "string" ? { topic: s, subtopics: [] } : { ...s },
              ),
            },
          ],
        },
      ];
    } else if (groupBy === "topic") {
      const topicMap = new Map();
      pages.forEach((p) => {
        (p.topics || []).forEach((t) => {
          const key = t.topic || "";
          if (!topicMap.has(key)) {
            topicMap.set(key, { ...t, subtopics: [...(t.subtopics || [])] });
          } else {
            topicMap.get(key).subtopics.push(...(t.subtopics || []));
          }
        });
      });
      basePages = [{ title: "", topics: Array.from(topicMap.values()) }];
    } else if (groupBy === "page") {
      basePages = pages.map((p, idx) => ({
        title: p.title,
        topics: [
          {
            topic: p.tag || p.title || `Page ${idx + 1}`,
            subtopics: (p.topics || []).flatMap((t) => t.subtopics || []),
          },
        ],
      }));
    } else {
      basePages = pages;
    }
    const selection = pageSelectionRef.current;
    const { items } = trimCollectionBySelection(basePages, selection);
    return items;
  }, [bookTitle, groupBy, pageSelectionParam, pageTitle, pages, trimCollectionBySelection]);

  const slides = React.useMemo(() => {
    const arr = [];
    arr.push({
      type: "title",
      text: pageTitle || bookTitle || "Present",
      gradStart,
      gradEnd,
      color: topicColor,
      iconColor,
      verseColor,
      icon: getIconForText(pageTitle || bookTitle || "Present"),
    });
    displayPages.forEach((p) => {
      (p.topics || []).forEach((t) => {
        const tGradStart = t.gradStart || gradStart;
        const tGradEnd = t.gradEnd || gradEnd;
        const tColor = t.topicColor || topicColor;
        const tIconColor = t.iconColor || iconColor;
        arr.push({
          type: "topic",
          text: t.topic,
          icon: getIconForText(t.topic, t.icon),
          verse: t.verse,
          gradStart: tGradStart,
          gradEnd: tGradEnd,
          color: tColor,
          iconColor: tIconColor,
          verseColor,
        });
        (t.subtopics || []).forEach((s) => {
          const data = typeof s === "string" ? { topic: s } : s;
          const sGradStart =
            data.gradStart || (useSubGradient ? tGradStart : undefined);
          const sGradEnd =
            data.gradEnd || (useSubGradient ? tGradEnd : undefined);
          const sColor = data.color || subColor;
          const sIconColor = data.iconColor || tIconColor;
          const tileColor =
            !sGradStart && !sGradEnd ? subTileColor || undefined : undefined;
          arr.push({
            type: "subtopic",
            text: data.topic,
            icon: getIconForText(data.topic, data.icon),
            verse: data.verse,
            gradStart: sGradStart,
            gradEnd: sGradEnd,
            color: sColor,
            iconColor: sIconColor,
            verseColor,
            tileColor,
          });
        });
      });
    });
    return arr;
  }, [
    displayPages,
    pageTitle,
    bookTitle,
    gradStart,
    gradEnd,
    topicColor,
    subColor,
    iconColor,
    verseColor,
    subTileColor,
    useSubGradient,
  ]);

  const topicSlides = React.useMemo(() => {
    const arr = [];
    arr.push({
      type: "title",
      text: pageTitle || bookTitle || "Present",
      gradStart,
      gradEnd,
      color: topicColor,
      iconColor,
      icon: getIconForText(pageTitle || bookTitle || "Present"),
    });
    displayPages.forEach((p) => {
      (p.topics || []).forEach((t) => {
        const tGradStart = t.gradStart || gradStart;
        const tGradEnd = t.gradEnd || gradEnd;
        const tColor = t.topicColor || topicColor;
        const tIconColor = t.iconColor || iconColor;
        const subtopics = (t.subtopics || []).map((s) => {
          const data = typeof s === "string" ? { topic: s } : s;
          const sGradStart =
            data.gradStart || (useSubGradient ? tGradStart : undefined);
          const sGradEnd =
            data.gradEnd || (useSubGradient ? tGradEnd : undefined);
          return {
            topic: data.topic,
            icon: getIconForText(data.topic, data.icon),
            color: data.color || subColor,
            iconColor: data.iconColor || tIconColor,
            gradStart: sGradStart,
            gradEnd: sGradEnd,
            tileColor:
              !sGradStart && !sGradEnd ? subTileColor || undefined : undefined,
          };
        });
        arr.push({
          type: "topicCard",
          text: t.topic,
          icon: getIconForText(t.topic, t.icon),
          gradStart: useSubGradient ? tGradStart : undefined,
          gradEnd: useSubGradient ? tGradEnd : undefined,
          tileColor:
            !useSubGradient ? subTileColor || undefined : undefined,
          color: tColor,
          iconColor: tIconColor,
          subtopics,
        });
      });
    });
    return arr;
  }, [
    displayPages,
    pageTitle,
    bookTitle,
    gradStart,
    gradEnd,
    topicColor,
    subColor,
    iconColor,
    subTileColor,
    useSubGradient,
  ]);

  const startPresentation = React.useCallback(
    (mode) => {
      if (!mode) return;
      const baseSlides = mode === "play-topic" ? topicSlides : slides;
      const { slides: nextSlides, selectionApplied } =
        applySlideSelection(baseSlides);
      if (!nextSlides.length) {
        pendingModeRef.current = mode;
        return;
      }
      pendingModeRef.current = null;
      const shouldDisableLoop =
        selectionApplied || defaultSlideLoopDisabled;
      setSlideLoopDisabled(shouldDisableLoop);
      if (presentationMode === mode && showSlideshow) {
        setSlideData(nextSlides);
        setCurrentSlide(0);
        setIsPlaying(false);
        return;
      }
      setSlideData(nextSlides);
      setCurrentSlide(0);
      setShowSlideshow(true);
      setIsPlaying(false);
      setPresentationMode(mode);
    },
    [
      applySlideSelection,
      defaultSlideLoopDisabled,
      presentationMode,
      showSlideshow,
      slides,
      topicSlides,
    ],
  );

  const stopPresentation = React.useCallback(() => {
    resetPresentationState();
  }, [resetPresentationState]);

  const goToTilesView = React.useCallback(() => {
    stopPresentation();
    skipQuerySyncRef.current = true;
    setShowTiles(true);
  }, [stopPresentation]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const headerLogo = document.querySelector(".header-logo");
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = "pointer";
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setTopControlsVisible((prev) => !prev);
      setControlsOpen((prev) => !prev);
    };
    headerLogo.addEventListener("click", handleHeaderClick);
    return () => {
      headerLogo.removeEventListener("click", handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleKeyToggle = (event) => {
      if (
        event.key === "Control" &&
        !event.repeat &&
        !event.altKey &&
        !event.metaKey
      ) {
        const activeElement = document.activeElement;
        const activeTag = activeElement?.tagName?.toLowerCase();
        if (
          activeElement?.isContentEditable ||
          ["input", "textarea", "select"].includes(activeTag || "")
        ) {
          return;
        }
        setControlsOpen((prev) => !prev);
        setTopControlsVisible((prev) => !prev);
        return;
      }
      if (event.key === "g" || event.key === "G") {
        goToTilesView();
        return;
      }
      if (event.key === "Escape" && controlsOpen) {
        setControlsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyToggle);
    return () => window.removeEventListener("keydown", handleKeyToggle);
  }, [controlsOpen, goToTilesView]);

  useEffect(() => {
    if (!presentationMode) return;
    const baseSlides = presentationMode === "play-topic" ? topicSlides : slides;
    const { slides: nextSlides, selectionApplied } =
      applySlideSelection(baseSlides);
    if (!nextSlides.length) {
      stopPresentation();
      return;
    }
    const shouldDisableLoop =
      selectionApplied || defaultSlideLoopDisabled;
    setSlideData(nextSlides);
    setCurrentSlide(0);
    setSlideLoopDisabled(shouldDisableLoop);
  }, [
    applySlideSelection,
    defaultSlideLoopDisabled,
    presentationMode,
    slides,
    stopPresentation,
    topicSlides,
  ]);

  useEffect(() => {
    if (!showSlideshow) {
      setOverlayControlsVisible(false);
    }
  }, [showSlideshow]);

  useEffect(() => {
    if (!showSlideshow) {
      setSlideLoopDisabled(defaultSlideLoopDisabled);
    }
  }, [defaultSlideLoopDisabled, showSlideshow]);

  useEffect(() => {
    if (skipQuerySyncRef.current) {
      skipQuerySyncRef.current = false;
      lastSearchRef.current = location.search;
      lastSyncedSearchRef.current = location.search.replace(/^\?/, "");
      queryInitialisedRef.current = true;
      return;
    }
    const params = new URLSearchParams(location.search);
    const currentSearch = location.search;
    const isNewSearch = currentSearch !== lastSearchRef.current;
    if (isNewSearch) {
      queryInitialisedRef.current = false;
      lastSearchRef.current = currentSearch;
    }
    const isInitialPass = !queryInitialisedRef.current;
    const tilesFallback = true;
    const controlsFallback = false;
    const descriptionsFallback = false;
    const versesFallback = true;
    const subGradientFallback = false;
    const slideGradientFallback = true;
    const centerFallback = false;
    const transparentFallback = false;
    const groupByFallback = "page-topic";
    const subViewFallback = "round";
    const videoFallback = "page";
    const transitionFallback = "fade";
    const topicsPerPageFallback = 4;
    const subsPerBoxFallback = 20;
    const gifDelayFallback = 2000;
    const cardWidthFallback = 400;
    const iconSizeFallback = 16;
    const textSizeFallback = 14;
    const categoryFallback = "all";
    const pageTitleFallback = "";
    const bookTitleFallback = "";

    const applyBoolean = (key, setter, currentValue, fallback) => {
      const raw = params.get(key);
      if (raw === null) {
        if (isInitialPass && currentValue !== fallback) {
          setter(fallback);
        }
        return;
      }
      const next = parseBooleanParam(raw, fallback);
      if (next !== currentValue) setter(next);
    };

    const applyNumber = (key, setter, currentValue, fallback) => {
      const raw = params.get(key);
      if (raw === null) {
        if (isInitialPass && currentValue !== fallback) {
          setter(fallback);
        }
        return;
      }
      const next = parseNumberParam(raw, fallback);
      if (next !== currentValue) setter(next);
    };

    const applyString = (key, setter, currentValue, fallback) => {
      const raw = params.get(key);
      if (raw === null) {
        if (isInitialPass && fallback !== undefined && currentValue !== fallback) {
          setter(fallback);
        }
        return;
      }
      if (raw !== currentValue) setter(raw);
    };

    applyBoolean("tiles", setShowTiles, showTiles, tilesFallback);
    applyBoolean("controls", setControlsOpen, controlsOpen, controlsFallback);
    applyBoolean(
      "descriptions",
      setShowDescriptions,
      showDescriptions,
      descriptionsFallback,
    );
    applyBoolean("verses", setShowVerses, showVerses, versesFallback);
    applyBoolean(
      "subGradient",
      setUseSubGradient,
      useSubGradient,
      subGradientFallback,
    );
    applyBoolean(
      "slideGradient",
      setUseSlideGradient,
      useSlideGradient,
      slideGradientFallback,
    );
    applyBoolean("center", setCenterAlign, centerAlign, centerFallback);
    applyBoolean(
      "transparent",
      setTransparentBg,
      transparentBg,
      transparentFallback,
    );

    applyNumber(
      "topicsPerPage",
      setTopicsPerPage,
      topicsPerPage,
      topicsPerPageFallback,
    );
    applyNumber("subsPerBox", setSubsPerBox, subsPerBox, subsPerBoxFallback);
    applyNumber("gifDelay", setGifDelay, gifDelay, gifDelayFallback);
    applyNumber("cardWidth", setCardWidth, cardWidth, cardWidthFallback);
    applyNumber("iconSize", setIconSize, iconSize, iconSizeFallback);
    applyNumber("textSize", setTextSize, textSize, textSizeFallback);

    applyString("groupBy", setGroupBy, groupBy, groupByFallback);
    applyString("subView", setSubViewType, subViewType, subViewFallback);
    applyString("videoMode", setVideoMode, videoMode, videoFallback);
    applyString(
      "transition",
      setTransitionEffect,
      transitionEffect,
      transitionFallback,
    );
    applyString(
      "category",
      setSelectedCategory,
      selectedCategory,
      categoryFallback,
    );
    applyString("pageTitle", setPageTitle, pageTitle, pageTitleFallback);
    applyString("bookTitle", setBookTitle, bookTitle, bookTitleFallback);

    const disableLoopRaw =
      params.get("disableLoop") || params.get("loop") || null;
    const disableLoopValue =
      disableLoopRaw === null
        ? false
        : parseBooleanParam(disableLoopRaw, false);
    if (disableLoopValue !== defaultSlideLoopDisabled) {
      setDefaultSlideLoopDisabled(disableLoopValue);
      if (!showSlideshow && slideLoopDisabled !== disableLoopValue) {
        setSlideLoopDisabled(disableLoopValue);
      }
    }

    const schemeParam = params.get("scheme");
    if (schemeParam) {
      if (schemeParam !== selectedScheme) {
        setSelectedScheme(schemeParam);
        applyPresetScheme(schemeParam);
      }
    } else if (isInitialPass && selectedScheme !== "custom") {
      setSelectedScheme("custom");
    }

    const masterParam = params.get("master");
    if (masterParam && masterParam !== masterColor) {
      setMasterColor(masterParam);
      applyMasterColor(masterParam);
      setSelectedScheme("custom");
    }

    const gradStartParam = params.get("gradStart");
    if (gradStartParam && gradStartParam !== gradStart) setGradStart(gradStartParam);
    else if (
      isInitialPass &&
      !gradStartParam &&
      gradStart !== PRESENTATION_DEFAULTS.gradStart
    )
      setGradStart(PRESENTATION_DEFAULTS.gradStart);
    const gradEndParam = params.get("gradEnd");
    if (gradEndParam && gradEndParam !== gradEnd) setGradEnd(gradEndParam);
    else if (
      isInitialPass &&
      !gradEndParam &&
      gradEnd !== PRESENTATION_DEFAULTS.gradEnd
    )
      setGradEnd(PRESENTATION_DEFAULTS.gradEnd);
    const topicColorParam = params.get("topicColor");
    if (topicColorParam && topicColorParam !== topicColor)
      setTopicColor(topicColorParam);
    else if (
      isInitialPass &&
      !topicColorParam &&
      topicColor !== PRESENTATION_DEFAULTS.topicColor
    )
      setTopicColor(PRESENTATION_DEFAULTS.topicColor);
    const subColorParam = params.get("subColor");
    if (subColorParam && subColorParam !== subColor) setSubColor(subColorParam);
    else if (
      isInitialPass &&
      !subColorParam &&
      subColor !== PRESENTATION_DEFAULTS.subColor
    )
      setSubColor(PRESENTATION_DEFAULTS.subColor);
    const verseColorParam = params.get("verseColor");
    if (verseColorParam && verseColorParam !== verseColor)
      setVerseColor(verseColorParam);
    else if (isInitialPass && !verseColorParam && verseColor !== defaultVerseColor)
      setVerseColor(defaultVerseColor);
    const iconColorParam = params.get("iconColor");
    if (iconColorParam && iconColorParam !== iconColor) setIconColor(iconColorParam);
    else if (
      isInitialPass &&
      !iconColorParam &&
      iconColor !== PRESENTATION_DEFAULTS.iconColor
    )
      setIconColor(PRESENTATION_DEFAULTS.iconColor);
    const subTileParam = params.get("subTileColor");
    if (subTileParam !== null && subTileParam !== subTileColor)
      setSubTileColor(subTileParam);
    else if (
      isInitialPass &&
      subTileParam === null &&
      subTileColor !== PRESENTATION_DEFAULTS.subTileColor
    )
      setSubTileColor(PRESENTATION_DEFAULTS.subTileColor);
    const tileBorderParam = params.get("tileBorderColor");
    if (tileBorderParam && tileBorderParam !== tileBorderColor)
      setTileBorderColor(tileBorderParam);
    else if (
      isInitialPass &&
      !tileBorderParam &&
      tileBorderColor !== defaultTileBorderColor
    )
      setTileBorderColor(defaultTileBorderColor);

    const jsonParam = params.get("json");
    const fileParam = params.get("file");
    const allowAutoHideTiles = !params.has("tiles");

    if (jsonParam) {
      try {
        const decoded = decodeURIComponent(jsonParam);
        if (decoded !== jsonContentFromQueryRef.current) {
          jsonContentFromQueryRef.current = decoded;
          setInput(decoded);
          setJsonQuery("");
          setSelectedFile("");
          lastLoadedFileRef.current = "";
          handleGenerate(decoded);
          if (allowAutoHideTiles && isInitialPass) setShowTiles(false);
          setTimeout(async () => {
            const images = await exportPagesData();
            if (images.length) {
              if (window.opener) window.opener.postMessage({ images }, "*");
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({ images }, "*");
              }
            }
          }, 100);
        }
      } catch (err) {
        console.error("JSON param error", err);
      }
    } else {
      jsonContentFromQueryRef.current = "";
      if (!fileParam) {
        if (queryInitialisedRef.current && selectedFile) {
          setSelectedFile("");
          setJsonQuery("");
          lastLoadedFileRef.current = "";
        }
      } else {
        const match = findSampleBySlug(fileParam);
        if (match) {
          const baseName = match.name.replace(/\.json$/, "");
          if (baseName !== lastLoadedFileRef.current) {
            const jsonStr = JSON.stringify(match.data, null, 2);
            lastLoadedFileRef.current = baseName;
            setInput(jsonStr);
            setJsonQuery(baseName);
            handleGenerate(jsonStr);
          }
          if (selectedFile !== baseName) setSelectedFile(baseName);
          if (allowAutoHideTiles && isInitialPass) setShowTiles(false);
        } else if (fileParam !== selectedFile) {
          setSelectedFile(fileParam);
        }
      }
    }

    const slideSelectionRaw = params.get("slides") || params.get("slide") || "";
    const parsedSlideSelection = parseSlideSelection(slideSelectionRaw);
    slideSelectionRef.current = parsedSlideSelection;
    setSlideSelectionParam(parsedSlideSelection ? slideSelectionRaw : "");

    const pageSelectionRaw =
      params.get("pages") || params.get("pageRange") || params.get("page") || "";
    const parsedPageSelection = parseSlideSelection(pageSelectionRaw);
    pageSelectionRef.current = parsedPageSelection;
    setPageSelectionParam(parsedPageSelection ? pageSelectionRaw : "");

    const modeParam = normaliseModeParam(params.get("mode"));
    if (modeParam) {
      pendingModeRef.current = modeParam;
    } else if (lastModeParamRef.current) {
      pendingModeRef.current = null;
      if (presentationMode) {
        stopPresentation();
      }
    }
    lastModeParamRef.current = modeParam;

    queryInitialisedRef.current = true;
  }, [
    cardWidth,
    centerAlign,
    controlsOpen,
    defaultSlideLoopDisabled,
    exportPagesData,
    gradEnd,
    gradStart,
    gifDelay,
    handleGenerate,
    iconColor,
    iconSize,
    location.search,
    masterColor,
    pageTitle,
    bookTitle,
    presentationMode,
    selectedCategory,
    selectedFile,
    selectedScheme,
    slideSelectionParam,
    pageSelectionParam,
    slideSelectionParam,
    pageSelectionParam,
    showDescriptions,
    showTiles,
    showVerses,
    showSlideshow,
    subColor,
    subTileColor,
    subViewType,
    subsPerBox,
    textSize,
    tileBorderColor,
    topicsPerPage,
    transitionEffect,
    transparentBg,
    useSlideGradient,
    useSubGradient,
    verseColor,
    topicColor,
    videoMode,
    slideLoopDisabled,
    stopPresentation,
  ]);

  useEffect(() => {
    const pending = pendingModeRef.current;
    if (!pending) return;
    const baseSlides = pending === "play-topic" ? topicSlides : slides;
    const { slides: nextSlides } = applySlideSelection(baseSlides);
    if (!nextSlides.length) return;
    pendingModeRef.current = null;
    startPresentation(pending);
  }, [applySlideSelection, slides, startPresentation, topicSlides]);

  useEffect(() => {
    if (!queryInitialisedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.has("json")) {
      params.delete("json");
    }
    const setOrDelete = (key, value, defaultValue) => {
      if (value === undefined || value === null || value === "") {
        if (params.has(key)) params.delete(key);
        return;
      }
      if (defaultValue !== undefined && value === defaultValue) {
        if (params.has(key)) params.delete(key);
        return;
      }
      if (params.get(key) !== value) params.set(key, value);
    };

    setOrDelete("tiles", showTiles ? "1" : "0", "1");
    setOrDelete("controls", controlsOpen ? "1" : null);
    setOrDelete("descriptions", showDescriptions ? "1" : null);
    setOrDelete("verses", showVerses ? null : "0");
    setOrDelete("subGradient", useSubGradient ? "1" : null);
    setOrDelete("slideGradient", useSlideGradient ? null : "0");
    setOrDelete("center", centerAlign ? "1" : null);
    setOrDelete("transparent", transparentBg ? "1" : null);
    setOrDelete("groupBy", groupBy, "page-topic");
    setOrDelete("subView", subViewType, "round");
    setOrDelete("videoMode", videoMode, "page");
    setOrDelete("transition", transitionEffect, "fade");
    setOrDelete("topicsPerPage", String(topicsPerPage), "4");
    setOrDelete("subsPerBox", String(subsPerBox), "20");
    setOrDelete("gifDelay", String(gifDelay), "2000");
    setOrDelete("cardWidth", String(cardWidth), "400");
    setOrDelete("iconSize", String(iconSize), "16");
    setOrDelete("textSize", String(textSize), "14");
    setOrDelete("slides", slideSelectionParam || null);
    setOrDelete("pages", pageSelectionParam || null);
    setOrDelete("disableLoop", defaultSlideLoopDisabled ? "1" : null);
    setOrDelete("category", selectedCategory === "all" ? null : selectedCategory);
    setOrDelete("pageTitle", pageTitle || null);
    setOrDelete("bookTitle", bookTitle || null);
    setOrDelete("scheme", selectedScheme === "custom" ? null : selectedScheme);
    setOrDelete("master", masterColor, "#ff0000");
    setOrDelete("gradStart", gradStart, "#ffffff");
    setOrDelete("gradEnd", gradEnd, "#f0f0f0");
    setOrDelete("topicColor", topicColor, "#3f8760");
    setOrDelete("subColor", subColor, "#333333");
    setOrDelete("verseColor", verseColor, defaultVerseColor);
    setOrDelete("iconColor", iconColor, "#3f8760");
    setOrDelete("subTileColor", subTileColor || null);
    setOrDelete("tileBorderColor", tileBorderColor, defaultTileBorderColor);
    setOrDelete("file", selectedFile || null);
    setOrDelete("mode", presentationMode || null);

    const nextSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch === currentSearch) {
      if (lastSyncedSearchRef.current !== currentSearch) {
        lastSyncedSearchRef.current = currentSearch;
      }
      return;
    }
    if (lastSyncedSearchRef.current === nextSearch) {
      return;
    }
    skipQuerySyncRef.current = true;
    lastSyncedSearchRef.current = nextSearch;
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [
    cardWidth,
    centerAlign,
    controlsOpen,
    defaultTileBorderColor,
    defaultVerseColor,
    gradEnd,
    gradStart,
    gifDelay,
    groupBy,
    iconColor,
    iconSize,
    location.pathname,
    location.search,
    masterColor,
    navigate,
    pageTitle,
    bookTitle,
    presentationMode,
    selectedCategory,
    selectedFile,
    selectedScheme,
    showDescriptions,
    showTiles,
    showVerses,
    subColor,
    subTileColor,
    subViewType,
    subsPerBox,
    textSize,
    tileBorderColor,
    topicsPerPage,
    transitionEffect,
    transparentBg,
    input,
    useSlideGradient,
    useSubGradient,
    verseColor,
    videoMode,
    topicColor,
  ]);

  return (
      <div className="infographics-container">
        <SEO
          title="Infographics"
          description="Visual slide content for Bible chapters, topics, and teaching."
        />
        <SiteWatermark />
      {showTiles ? (
        <div className="tiles-view">
          {displayedGroups.map(([folder, files]) => (
            <div key={folder} className="tile-group">
              <h2 onClick={() => toggleGroup(folder)}>
                {collapsedGroups[folder] ? "▶" : "▼"} {toTitleCase(folder)}
              </h2>
              {!collapsedGroups[folder] && (
                <div className="tiles-grid">
                  {files.map((file) => {
                    const filePath = file.name.replace(/\.json$/, "");
                    const fileBase = filePath.split("/").pop();
                    const fileSlug = slugify(fileBase);
                    return (
                      <div
                        key={file.name}
                        className="tile-item"
                        data-folder={folder}
                        data-file-base={fileBase}
                        data-file-path={filePath}
                        data-file-slug={fileSlug}
                        title={fileBase}
                        onClick={() => handleTileClick(file)}
                      >
                        {fileBase}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className={`top-controls${topControlsVisible ? "" : " is-hidden"}`}>
            <div className="present-topic-select">
              <select value={topicLevel1} onChange={handleTopicLevel1Change}>
                <option value="">Topic Folder</option>
                {level1Options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                value={topicLevel2}
                onChange={handleTopicLevel2Change}
                disabled={!topicLevel1 || level2Options.length === 0}
              >
                <option value="">
                  {level2Options.length ? "Subfolder / File" : "—"}
                </option>
                {level2Options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <select
                value={topicLevel3}
                onChange={handleTopicLevel3Change}
                disabled={
                  !topicLevel1 ||
                  !topicLevel2 ||
                  level3Options.length === 0
                }
              >
                <option value="">
                  {level3Options.length ? "Remaining Path" : "—"}
                </option>
                {level3Options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="icon-button present-icon tile-toggle"
              onClick={goToTilesView}
              title="Tiles"
            >
              <FaGripHorizontal />
            </button>
            <button
              type="button"
              className="icon-button present-icon"
              onClick={(event) => {
                event.preventDefault();
                startPresentation("play");
              }}
              title="Play"
            >
              <FaPlay />
            </button>
            <button
              type="button"
              className="icon-button present-icon"
              onClick={(event) => {
                event.preventDefault();
                startPresentation("play-topic");
              }}
              title="Play Topic"
            >
              <FaPlayCircle />
            </button>
            {!showSlideshow && (
              null
            )}
            <button
              className="icon-button present-icon"
              onClick={handleExportBible}
              title="Export Bible"
            >
              <FaBible />
            </button>
            <button
              type="button"
              className="icon-button present-icon"
              onClick={() => setTopControlsVisible(false)}
              title="Close"
              aria-label="Close bottom navigation"
            >
              <FaTimes />
            </button>
          </div>
          <div className="infographic-pages" ref={pagesRef}>
        {displayPages.map((p, pIdx) => {
          const pageStyle = {
            background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`,
          };
          return (
            <div key={pIdx} className="infographic-page" style={pageStyle}>
              <div className="page-number">{pIdx + 1}</div>
              <img
                src="/logo192.png"
                alt="logo"
                className="infographics-logo"
              />
              {p.title && (
                <h2 className="infographic-title" style={{ color: topicColor }}>
                  {p.title}
                </h2>
              )}
              <section
                className="infographics-grid"
                ref={pIdx === 0 ? gridRef : null}
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${cardWidth}px, 1fr))`,
                }}
              >
                {p.topics.flatMap((item, idx) => {
                  const cardGradStart = item.gradStart || gradStart;
                  const cardGradEnd = item.gradEnd || gradEnd;
                  const cardTopicColor = item.topicColor || topicColor;
                  const cardSubColor = item.subtopicColor || subColor;
                  const cardIconColor = item.iconColor || iconColor;
                  const TopicIcon = getIconForText(item.topic, item.icon);
                  const subs = item.subtopics || [];
                  const chunks = [];
                  for (let i = 0; i < subs.length || i === 0; i += subsPerBox) {
                    chunks.push(subs.slice(i, i + subsPerBox));
                  }
                  return chunks.map((chunk, cIdx) => (
                    <div
                      key={`${idx}-${cIdx}`}
                      className={`infographic-card ${subViewType}-card`}
                      style={{
                        background: `linear-gradient(135deg, ${cardGradStart}, ${cardGradEnd})`,
                        ...(transparentBg ? { border: "none" } : {}),
                      }}
                    >
                      <h3
                        className="topic-header"
                        style={{
                          color: cardTopicColor,
                          fontSize: textSize + 2,
                          display: "flex",
                          alignItems: "center",
                          ...(centerAlign
                            ? {
                                flexDirection: "column",
                                justifyContent: "center",
                                textAlign: "center",
                              }
                            : {}),
                        }}
                      >
                    <TopicIcon
                      className="topic-icon"
                      title={item.topic}
                      style={{
                        color:    cardIconColor,
                        fontSize: `${tileIconSize}px`,
                        width:    `${tileIconSize}px`,
                        height:   `${tileIconSize}px`,
                        flex:     "0 0 auto",    // prevent shrinking
                      }}
                    />

                        <span
                          className="topic-text"
                          style={
                            centerAlign
                              ? {
                                  display: "block",
                                  width: "100%",
                                }
                              : {}
                          }
                        >
                          {item.topic}
                          {chunks.length > 1 ? ` (${cIdx + 1})` : ""}
                        </span>
                      </h3>
                      {showDescriptions && item.description && (
                        <p
                          className="topic-desc"
                          style={{ color: cardTopicColor, fontSize: textSize }}
                        >
                          {item.description}
                        </p>
                      )}
                      {renderSubtopics(
                        chunk,
                        0,
                        cardSubColor,
                        cardGradStart,
                        cardGradEnd,
                        cardIconColor,
                      )}
                    </div>
                  ));
                })}
              </section>
              <div className="infographic-footer">
                Page {pIdx + 1} • By GlobalChristianCommunity.com
              </div>
            </div>
          );
        })}
      </div>

      </>
      )}
      <div className={`controls-pane ${controlsOpen ? "open" : "collapsed"}`}>
        <div className="controls-pane__header">
          <span>Present Controls</span>
          <div className="controls-pane__actions">
            <button
              type="button"
              className={`controls-pane__toggle ${isPlaying ? "is-active" : ""}`}
              onClick={() => setIsPlaying((p) => !p)}
              title={isPlaying ? "Disable auto next" : "Enable auto next"}
              aria-pressed={isPlaying}
            >
              Auto Next
            </button>
            <button
              type="button"
              className="controls-pane__close"
              aria-label="Close controls panel"
              onClick={() => setControlsOpen(false)}
            >
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="controls-pane__body">
          <section className="infographics-input">
            <h1>Present Generator</h1>
            {sampleFiles.length > 0 && (
              <div className="json-select">
                <input
                  list="json-files"
                  value={jsonQuery}
                  onChange={handleJsonInput}
                  onFocus={(e) => {
                    focusJsonInput(e);
                  }}
                  onMouseDown={(e) => {
                    if (e.target.showPicker) {
                      try {
                        e.target.showPicker();
                      } catch {
                        /* ignored */
                      }
                    }
                  }}
                  onBlur={restoreJsonInput}
                  placeholder="Select Topic File"
                />
                <datalist id="json-files">
                  {sampleFiles.map((f) => (
                    <option key={f.name} value={f.name.replace(/\.json$/, "")}>
                      {f.name.replace(/\.json$/, "")}
                    </option>
                  ))}
                </datalist>
              </div>
            )}
            <div className="pptx-controls">
              <button className="export-btn" onClick={handlePlayPPTX}>
                Play to PowerPoint
              </button>
              <button className="export-btn" onClick={handlePlayTopicPPTX}>
                Play by Topic to PowerPoint
              </button>
            </div>
            <textarea
              value={input}
              onChange={(e) => {
                const val = e.target.value;
                setInput(val);
                handleGenerate(val);
                if (selectedFile) {
                  setSelectedFile("");
                }
                if (jsonQuery) {
                  setJsonQuery("");
                }
              }}
              placeholder="Enter Json Or Bullet List With Topics And Nested Subtopics"
            />
            <div className="title-input">
              <input
                type="text"
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
                placeholder="Page Title"
              />
            </div>
            <div className="gradient-controls">
              <label>
                Master
                <input
                  type="color"
                  value={masterColor}
                  onChange={(e) => {
                    setMasterColor(e.target.value);
                    applyMasterColor(e.target.value);
                    setSelectedScheme("custom");
                  }}
                />
              </label>
              <select value={selectedScheme} onChange={handleSchemeChange}>
                <option value="custom">Custom</option>
                {colorSchemes.map((g) => (
                  <option key={g.name} value={g.name}>
                    {toTitleCase(g.name)}
                  </option>
                ))}
              </select>
              <>
                <label>
                  Start
                  <input
                    type="color"
                    value={gradStart}
                    onChange={(e) => setGradStart(e.target.value)}
                  />
                </label>
                <label>
                  End
                  <input
                    type="color"
                    value={gradEnd}
                    onChange={(e) => setGradEnd(e.target.value)}
                  />
                </label>
                <label>
                  Topic
                  <input
                    type="color"
                    value={topicColor}
                    onChange={(e) => setTopicColor(e.target.value)}
                  />
                </label>
                <label>
                  Subtopic
                  <input
                    type="color"
                    value={subColor}
                    onChange={(e) => setSubColor(e.target.value)}
                  />
                </label>
                <label>
                  Verse
                  <input
                    type="color"
                    value={verseColor}
                    onChange={(e) => setVerseColor(e.target.value)}
                  />
                </label>
                <label>
                  Icon
                  <input
                    type="color"
                    value={iconColor}
                    onChange={(e) => setIconColor(e.target.value)}
                  />
                </label>
                <label>
                  Tile BG
                  <input
                    type="color"
                    value={subTileColor || "#ffffff"}
                    onChange={(e) => setSubTileColor(e.target.value)}
                  />
                </label>
              </>
            </div>
            <div className="view-controls">
              <select value={subViewType} onChange={handleViewChange}>
                {subViewTypes.map((v) => (
                  <option key={v} value={v}>
                    {toTitleCase(v)}
                  </option>
                ))}
              </select>
              <label>
                Width
                <input
                  type="range"
                  min="180"
                  max="600"
                  value={cardWidth}
                  onChange={handleWidthChange}
                />
              </label>
              <label>
                Icon Size ({iconSize})
                <input
                  type="range"
                  min="12"
                  max="48"
                  value={iconSize}
                  onChange={(e) => setIconSize(parseInt(e.target.value, 10))}
                />
              </label>
              <label>
                Text Size ({textSize})
                <input
                  type="range"
                  min="10"
                  max="32"
                  value={textSize}
                  onChange={(e) => setTextSize(parseInt(e.target.value, 10))}
                />
              </label>
              <label>
                Subs per box ({subsPerBox})
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={subsPerBox}
                  onChange={(e) => setSubsPerBox(parseInt(e.target.value, 10))}
                />
              </label>
              <label>
                Topics per page
                <input
                  type="number"
                  min="0"
                  value={topicsPerPage}
                  onChange={(e) =>
                    setTopicsPerPage(parseInt(e.target.value, 10) || 0)
                  }
                />
              </label>
              <label>
                Group By
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <option value="page-topic">Page/Topic</option>
                  <option value="topic">Topic</option>
                  <option value="page">Page</option>
                  <option value="none">None</option>
                </select>
              </label>
              <label>
                Show Descriptions
                <input
                  type="checkbox"
                  checked={showDescriptions}
                  onChange={toggleDescriptions}
                />
              </label>
              <label>
                Show Verses
                <input
                  type="checkbox"
                  checked={showVerses}
                  onChange={toggleVerses}
                />
              </label>
              <label>
                Gradient Tiles
                <input
                  type="checkbox"
                  checked={useSubGradient}
                  onChange={() => setUseSubGradient((p) => !p)}
                />
              </label>
              <label>
                Gradient Slides
                <input
                  type="checkbox"
                  checked={useSlideGradient}
                  onChange={() => setUseSlideGradient((p) => !p)}
                />
              </label>
              <label>
                Center Align
                <input
                  type="checkbox"
                  checked={centerAlign}
                  onChange={() => setCenterAlign((p) => !p)}
                />
              </label>
              <label>
                Transparent Export
                <input
                  type="checkbox"
                  checked={transparentBg}
                  onChange={() => setTransparentBg((p) => !p)}
                />
              </label>
              <label>
                Slide Delay (ms)
                <input
                  type="number"
                  min="200"
                  value={gifDelay}
                  onChange={(e) => setGifDelay(parseInt(e.target.value, 10))}
                />
                <input
                  type="range"
                  min="200"
                  max="10000"
                  step="200"
                  value={gifDelay}
                  onChange={(e) => setGifDelay(parseInt(e.target.value, 10))}
                />
              </label>
              <label>
                Video Mode
                <select
                  value={videoMode}
                  onChange={(e) => setVideoMode(e.target.value)}
                >
                  <option value="page">Per Page</option>
                  <option value="topic">Per Topic</option>
                </select>
              </label>
              <label>
                Transition
                <input
                  type="text"
                  value={transitionEffect}
                  onChange={(e) => setTransitionEffect(e.target.value)}
                />
              </label>
            </div>
            {/* prevent the click event object from being passed to handleGenerate */}
            <button onClick={() => handleGenerate()}>Generate</button>
            {displayPages.length > 0 &&
              displayPages[0].topics &&
              displayPages[0].topics.length > 0 && (
                <div className="export-actions">
                  <button className="export-btn" onClick={handleExport}>
                    Export As Image
                  </button>
                  <button className="export-btn" onClick={handleExportPages}>
                    Export Image By Page
                  </button>
                  <button className="export-btn" onClick={handleExportAll}>
                    Export All As Single Image
                  </button>
                  <button className="export-btn" onClick={handleExportPDF}>
                    Export As PDF
                  </button>
                  <button className="export-btn" onClick={handleExportGIF}>
                    Export GIFs
                  </button>
                  <button className="export-btn" onClick={handleExportPPTX}>
                    Export PowerPoint
                  </button>
                  <button className="export-btn" onClick={handleExportDOC}>
                    Export DOC
                  </button>
                  <button className="export-btn" onClick={handleExportVideo}>
                    Export Video
                  </button>
                </div>
              )}
          </section>
        </div>
      </div>
      {showSlideshow && (
        <SlideshowOverlay
          slides={slideData}
          onClose={stopPresentation}
          controlsVisible={overlayControlsVisible}
          onToggleControls={() => setOverlayControlsVisible((prev) => !prev)}
          disableLoop={slideLoopDisabled}
        />
      )}
    </div>
  );
}

export default Infographics;
